import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Card, Paper, IconButton, Chip, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Grid, MenuItem, Menu, FormControlLabel,
  Checkbox, Alert, Snackbar, Divider, Stack, LinearProgress, Drawer, Tabs, Tab,
  Tooltip, ButtonGroup, Badge, Autocomplete, CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon, ChevronLeft, ChevronRight, CalendarToday, CalendarMonth as CalendarMonthIcon,
  LocalHospital, AccessTime, Inventory2, Article, Pets as PetsIcon,
  ChecklistRtl as ChecklistIcon, ViewList as ViewListIcon, Print as PrintIcon,
  Warning as WarningIcon, Timer as TimerIcon, MedicalServices as MedicalServicesIcon,
  Close, Person as PersonIcon, MeetingRoom as MeetingRoomIcon,
} from '@mui/icons-material';
import { getCollection, getDocument, addDocument, updateDocument } from '../services/firestoreService';
import { db } from '../firebase';
import { collection, doc, getDoc, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import { useBranch } from '../context/BranchContext';
import PatientSearch from '../components/PatientSearch';
import SearchableSelect from '../components/SearchableSelect';
import { requestToSurgeryForm, getPriorityConfig } from '../models/surgeryModel';

// ─── Design system ─────────────────────────────────────────────────────────────
import { COLORS } from '../theme/constants';

const { primary: PRIMARY, teal: TEAL, warning: AMBER, danger: DANGER, success: SUCCESS, info: INFO } = COLORS;

// ─── Grid constants ────────────────────────────────────────────────────────────
const ROW_HEIGHT  = 80;  // px per hour
const FIRST_HOUR  = 8;
const TOTAL_HOURS = 11;  // 8–18

// Colores predefinidos para pabellones
const PABELLON_COLORS = ['#1976d2', '#9c27b0', '#f57c00', '#4caf50', '#ff5722', '#00bcd4', '#e91e63', '#795548'];

// Horario de trabajo (8 AM a 6 PM)
const WORK_HOURS = Array.from({ length: TOTAL_HOURS }, (_, i) => i + FIRST_HOUR);

// ─── ASA options ──────────────────────────────────────────────────────────────
const ASA_OPTIONS = [
  { value: 'I',    label: 'I — Paciente normal sano' },
  { value: 'II',   label: 'II — Enfermedad sistémica leve' },
  { value: 'III',  label: 'III — Enfermedad sistémica grave' },
  { value: 'IV',   label: 'IV — Riesgo de vida' },
  { value: 'V',    label: 'V — Paciente moribundo' },
  { value: 'IE',   label: 'I E — Emergencia, paciente sano' },
  { value: 'IIE',  label: 'II E — Emergencia, enf. leve' },
  { value: 'IIIE', label: 'III E — Emergencia, enf. grave' },
  { value: 'IVE',  label: 'IV E — Emergencia, riesgo vital' },
];

// Estados de cirugía
const SURGERY_STATES = [
  { value: 'programada', label: 'Programada', color: '#ffd700' },
  { value: 'confirmada', label: 'Confirmada', color: '#4caf50' },
  { value: 'pre-operatorio', label: 'Pre-operatorio', color: '#2196f3' },
  { value: 'en-quirofano', label: 'En Quirófano', color: '#9c27b0' },
  { value: 'post-operatorio', label: 'Post-operatorio', color: '#ff9800' },
  { value: 'completada', label: 'Completada', color: '#4caf50' },
  { value: 'pospuesta', label: 'Pospuesta', color: '#9e9e9e' },
  { value: 'cancelada', label: 'Cancelada', color: '#f44336' }
];

// Categorías de procedimientos
const PROCEDURE_CATEGORIES = [
  'Tejidos Blandos',
  'Ortopedia',
  'Dental',
  'Oftalmología',
  'Neurología',
  'Oncología',
  'Emergencia',
  'Otro'
];

// ─── Helper ────────────────────────────────────────────────────────────────────
// Devuelve la fecha en formato YYYY-MM-DD usando la zona horaria local (no UTC).
// toISOString() devuelve UTC y causa que despues de cierta hora la fecha cambie al dia siguiente.
const toLocalDateStr = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const getSurgeryStyle = (surgery) => {
  const [startH, startM] = (surgery.startTime || '08:00').split(':').map(Number);
  const durationMin = surgery.duration || 60;
  const topPx    = (startH - FIRST_HOUR) * ROW_HEIGHT + (startM / 60) * ROW_HEIGHT;
  const heightPx = Math.max((durationMin / 60) * ROW_HEIGHT, 44);
  return { top: topPx, height: heightPx };
};

// ─── SurgeryCard ───────────────────────────────────────────────────────────────
function SurgeryCard({ surgery, roomName, hasAlert, isRunning, onEdit, onContextMenu,
                       onSupplies, onProtocol, onRecord, onChecklist,
                       draggingSurgeryId, setDraggingSurgeryId, tableIndex = 0, tableCount = 1 }) {
  const stateConfig = SURGERY_STATES.find(s => s.value === surgery.status) || {};
  const style       = getSurgeryStyle(surgery);
  const colCount    = Math.max(1, tableCount || 1);
  const colIndex    = Math.min(Math.max(0, tableIndex || 0), colCount - 1);
  const colWidth    = 100 / colCount;
  const priorityCfg = getPriorityConfig(surgery.priority);
  const isPriorityHigh = surgery.priority === 'Urgente' || surgery.priority === 'Emergencia';
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!isRunning || !surgery.surgeryStartAt) return;
    const tick = () => {
      const start = surgery.surgeryStartAt?.toDate?.() || new Date(surgery.surgeryStartAt);
      setElapsed(Math.floor((new Date() - start) / 60000));
    };
    tick();
    const id = setInterval(tick, 30000);
    return () => clearInterval(id);
  }, [isRunning, surgery.surgeryStartAt]);

  const isOverTime = isRunning && elapsed > (surgery.duration || 60);
  const hasCriticalAlert = surgery.patientAlergias;
  const borderColor = hasCriticalAlert ? DANGER : hasAlert ? AMBER : 'transparent';

  // Tooltip con el detalle completo — útil cuando el bloque es corto
  const tooltipContent = (
    <Box sx={{ p: 0.5 }}>
      <Typography sx={{ fontWeight: 800, fontSize: '0.8rem', mb: 0.3 }}>
        🐾 {surgery.patientName}
      </Typography>
      <Typography sx={{ fontSize: '0.72rem' }}>{surgery.procedureType || 'Procedimiento sin especificar'}</Typography>
      <Divider sx={{ my: 0.6, borderColor: 'rgba(255,255,255,0.25)' }} />
      <Typography sx={{ fontSize: '0.7rem' }}>🕐 {surgery.startTime} → {surgery.endTime} ({surgery.duration} min)</Typography>
      {roomName && <Typography sx={{ fontSize: '0.7rem' }}>🏥 {roomName}{surgery.surgicalTable ? ` · ${surgery.surgicalTable}` : ''}</Typography>}
      {surgery.mainSurgeon && <Typography sx={{ fontSize: '0.7rem' }}>👨‍⚕️ {surgery.mainSurgeon}</Typography>}
      {surgery.anesthesiologist && <Typography sx={{ fontSize: '0.7rem' }}>💉 {surgery.anesthesiologist}</Typography>}
      <Typography sx={{ fontSize: '0.7rem' }}>
        ⚑ {priorityCfg.label}{surgery.asaScore ? ` · ASA ${surgery.asaScore}` : ''} · {stateConfig.label || surgery.status}
      </Typography>
      {surgery.ownerConvenio && (
        <Typography sx={{ fontSize: '0.7rem' }}>🏷️ Convenio: {surgery.ownerConvenio}</Typography>
      )}
      {surgery.patientAlergias && (
        <Typography sx={{ fontSize: '0.7rem', color: '#FCA5A5', fontWeight: 700 }}>⚠ Alérgico: {surgery.patientAlergias}</Typography>
      )}
      {surgery.budgetNumber && surgery.presupuestoEstado && surgery.presupuestoEstado !== 'pagado' && (
        <Typography sx={{ fontSize: '0.7rem', color: '#FBBF24', fontWeight: 700 }}>⚠ Presupuesto no pagado ({surgery.presupuestoEstado})</Typography>
      )}
    </Box>
  );

  return (
    <Tooltip title={tooltipContent} arrow placement="right"
      componentsProps={{ tooltip: { sx: { bgcolor: 'rgba(15,23,42,0.96)', maxWidth: 260 } }, arrow: { sx: { color: 'rgba(15,23,42,0.96)' } } }}>
    <Paper elevation={2} sx={{
      position: 'absolute', top: style.top, height: style.height,
      left: `calc(${colIndex * colWidth}% + 4px)`, right: `calc(${(100 - (colIndex + 1) * colWidth)}% + 4px)`,
      bgcolor: stateConfig.color || '#9e9e9e',
      color: 'white', borderRadius: 1.5, overflow: 'hidden', cursor: 'grab', zIndex: 2,
      border: `2px solid ${borderColor}`,
      boxShadow: isRunning ? `0 0 0 2px ${isOverTime ? DANGER : SUCCESS}` : undefined,
      opacity: draggingSurgeryId === surgery.id ? 0.65 : 1,
      transition: 'opacity 0.15s',
      '&:hover': { zIndex: 20 },
      '& .card-actions': { opacity: 0, pointerEvents: 'none' },
      '&:hover .card-actions': { opacity: 1, pointerEvents: 'auto' },
    }}
      onMouseDown={e => { if (e.button !== 0) return; e.stopPropagation(); setDraggingSurgeryId(surgery.id); }}
      onClick={e => { e.stopPropagation(); onEdit(surgery); }}
      onContextMenu={e => onContextMenu(e, surgery)}
    >
      {/* Alert bar */}
      <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 4,
        bgcolor: hasCriticalAlert ? DANGER : hasAlert ? AMBER : SUCCESS }}/>

      <Box sx={{ p: 0.5, pl: 1.1, overflow: 'hidden', height: '100%',
        display: 'flex', flexDirection: 'column', gap: 0.1 }}>
        {/* Allergy banner */}
        {surgery.patientAlergias && style.height > 56 && (
          <Box sx={{ bgcolor: 'rgba(0,0,0,0.28)', borderRadius: 0.5, px: 0.5 }}>
            <Typography noWrap sx={{ fontSize: '0.58rem', fontWeight: 800, lineHeight: 1.3 }}>
              ⚠ ALÉRGICO: {surgery.patientAlergias}
            </Typography>
          </Box>
        )}

        {/* Presupuesto no pagado banner */}
        {surgery.budgetNumber && surgery.presupuestoEstado && surgery.presupuestoEstado !== 'pagado' && style.height > 56 && (
          <Box sx={{ bgcolor: 'rgba(217,119,6,0.85)', borderRadius: 0.5, px: 0.5 }}>
            <Typography noWrap sx={{ fontSize: '0.58rem', fontWeight: 800, lineHeight: 1.3 }}>
              ⚠ PRESUPUESTO NO PAGADO ({surgery.presupuestoEstado})
            </Typography>
          </Box>
        )}

        {/* Patient + priority + ASA */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
          <Typography noWrap sx={{ fontWeight: 800, fontSize: '0.74rem', lineHeight: 1.15, flex: 1 }}>
            🐾 {surgery.patientName}
          </Typography>
          {surgery.ownerConvenio && (
            <Chip label={surgery.ownerConvenio} size="small"
              sx={{ height: 14, fontSize: '0.52rem', fontWeight: 700, flexShrink: 0, bgcolor: 'rgba(255,255,255,0.25)', color: 'white' }} />
          )}
          {isPriorityHigh && (
            <Chip label={priorityCfg.label} size="small"
              sx={{ height: 14, fontSize: '0.55rem', fontWeight: 800,
                bgcolor: priorityCfg.color, color: 'white',
                animation: surgery.priority === 'Emergencia' ? 'pulseChip 1.4s ease-in-out infinite' : 'none',
                '@keyframes pulseChip': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.55 } } }}/>
          )}
          {surgery.asaScore && (
            <Chip label={`ASA ${surgery.asaScore}`} size="small"
              sx={{ height: 14, fontSize: '0.58rem', bgcolor: 'rgba(255,255,255,0.25)', color: 'white' }}/>
          )}
        </Box>

        {/* Procedure — se muestra siempre que haya dato */}
        {surgery.procedureType && (
          <Typography noWrap sx={{ fontSize: '0.66rem', opacity: 0.95, lineHeight: 1.2 }}>
            {surgery.procedureType}
          </Typography>
        )}

        {/* Surgeon */}
        {style.height > 56 && surgery.mainSurgeon && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
            <PersonIcon sx={{ fontSize: 11, opacity: 0.9 }} />
            <Typography noWrap sx={{ fontSize: '0.63rem', opacity: 0.9 }}>{surgery.mainSurgeon}</Typography>
          </Box>
        )}

        {/* Room */}
        {style.height > 72 && roomName && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
            <MeetingRoomIcon sx={{ fontSize: 11, opacity: 0.9 }} />
            <Typography noWrap sx={{ fontSize: '0.63rem', opacity: 0.9 }}>
              {roomName}{surgery.surgicalTable ? ` · ${surgery.surgicalTable}` : ''}
            </Typography>
          </Box>
        )}

        {/* Time / timer */}
        {style.height > 88 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
            <AccessTime sx={{ fontSize: 11, opacity: 0.9 }} />
            <Typography sx={{ fontSize: '0.63rem', opacity: 0.9 }}>
              {isRunning ? (
                <span style={{ color: isOverTime ? '#FBBF24' : 'white', fontWeight: isOverTime ? 800 : 400 }}>
                  {elapsed}/{surgery.duration} min {isOverTime && '⚠'}
                </span>
              ) : `${surgery.startTime} → ${surgery.endTime}`}
            </Typography>
          </Box>
        )}

      </Box>

      {/* Acciones — barra flotante visible al pasar el mouse (independiente del alto) */}
      <Box className="card-actions" sx={{
        position: 'absolute', bottom: 0, left: 0, right: 0,
        display: 'flex', justifyContent: 'center', gap: 0.4, py: 0.3,
        bgcolor: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(1px)',
        transition: 'opacity 0.15s',
      }}>
        {[
          { title: 'Insumos',   icon: <Inventory2 sx={{ fontSize: 14 }}/>,     fn: onSupplies },
          { title: 'Protocolo', icon: <Article sx={{ fontSize: 14 }}/>,         fn: onProtocol },
          { title: 'Ficha',     icon: <PetsIcon sx={{ fontSize: 14 }}/>,        fn: onRecord   },
          { title: 'Checklist', icon: <ChecklistIcon sx={{ fontSize: 14 }}/>,   fn: onChecklist},
        ].map(({ title, icon, fn }) => (
          <Tooltip key={title} title={title}>
            <IconButton size="small"
              sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.2)', p: 0.35,
                '&:hover': { bgcolor: 'rgba(255,255,255,0.38)' } }}
              onMouseDown={e => e.stopPropagation()}
              onClick={e => { e.stopPropagation(); fn && fn(surgery); }}>
              {icon}
            </IconButton>
          </Tooltip>
        ))}
      </Box>
    </Paper>
    </Tooltip>
  );
}

// ─── SafetyChecklistDialog ─────────────────────────────────────────────────────
const CHECKLIST_PHASES = {
  signIn: {
    label: 'Sign In', subtitle: 'Antes de la inducción anestésica', color: INFO,
    items: {
      identidadVerificada:        'Identidad del paciente verificada',
      procedimientoConfirmado:    'Procedimiento y sitio quirúrgico confirmados',
      consentimientoObtenido:     'Consentimiento del propietario obtenido',
      alergiasRevisadas:          'Alergias revisadas con el equipo',
      ayunoConfirmado:            'Ayuno confirmado',
      examenesPreop:              'Exámenes pre-op disponibles y revisados',
      viaPermeable:               'Vía venosa permeable y funcional',
      equipoAnestesiaVerificado:  'Equipamiento de anestesia verificado',
      asaClasificado:             'Score ASA clasificado',
    },
  },
  timeOut: {
    label: 'Time Out', subtitle: 'Antes de la incisión — equipo completo presente', color: AMBER,
    items: {
      equipoPresentado:          'Todo el equipo se presentó por nombre y rol',
      procedimientoConfirmado:   'Procedimiento a realizar confirmado',
      sitioConfirmado:           'Sitio / lado del cuerpo confirmado',
      antibioticoAdministrado:   'Antibiótico profiláctico administrado (si aplica)',
      equipoCriticoDisponible:   'Equipamiento crítico disponible',
      imagenDisponible:          'Estudios de imagen disponibles (si aplica)',
      perdidaSangreEstimada:     'Pérdida de sangre anticipada estimada',
    },
  },
  signOut: {
    label: 'Sign Out', subtitle: 'Antes de que el paciente salga del pabellón', color: SUCCESS,
    items: {
      procedimientoRegistrado:   'Procedimiento realizado registrado',
      conteoInstrumentos:        'Conteo de instrumentos completado',
      muestrasEtiquetadas:       'Muestras histopatológicas etiquetadas',
      complicacionesRegistradas: 'Problemas/complicaciones registrados',
      planPostopConfirmado:      'Plan post-operatorio confirmado con el equipo',
    },
  },
};

function SafetyChecklistDialog({ open, onClose, surgery, onSave, userData }) {
  const [cl, setCl] = useState(surgery?.surgicalSafetyChecklist || { signIn: { items: {} }, timeOut: { items: {} }, signOut: { items: {} } });
  const [phase, setPhase] = useState('signIn');

  useEffect(() => {
    setCl(surgery?.surgicalSafetyChecklist || { signIn: { items: {} }, timeOut: { items: {} }, signOut: { items: {} } });
  }, [surgery]);

  const pctOf = (ph) => {
    const keys = Object.keys(CHECKLIST_PHASES[ph].items);
    const done = keys.filter(k => cl[ph]?.items?.[k]).length;
    return { done, total: keys.length, pct: Math.round((done / keys.length) * 100) };
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <ChecklistIcon color="primary"/>
        Checklist de Seguridad Quirúrgica — {surgery?.patientName}
        {surgery?.patientAlergias && (
          <Chip label={`⚠ ${surgery.patientAlergias}`} size="small" sx={{ bgcolor: DANGER, color: 'white', fontSize: '0.7rem' }}/>
        )}
        {surgery?.asaScore && (
          <Chip label={`ASA ${surgery.asaScore}`} size="small" sx={{ bgcolor: AMBER, color: 'white', fontSize: '0.7rem' }}/>
        )}
      </DialogTitle>
      <DialogContent>
        <Tabs value={phase} onChange={(_, v) => setPhase(v)} sx={{ mb: 2 }}>
          {Object.entries(CHECKLIST_PHASES).map(([key, ph]) => {
            const { done, total, pct } = pctOf(key);
            return (
              <Tab key={key} value={key}
                label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                  {ph.label}
                  <Chip label={`${done}/${total}`} size="small"
                    sx={{ height: 18, fontSize: '0.65rem', bgcolor: pct === 100 ? SUCCESS : AMBER, color: 'white' }}/>
                </Box>}/>
            );
          })}
        </Tabs>

        {Object.entries(CHECKLIST_PHASES).map(([key, ph]) => key === phase && (
          <Box key={key}>
            <Typography variant="caption" color="text.secondary" display="block" mb={1}>
              {ph.subtitle}
            </Typography>
            <LinearProgress variant="determinate" value={pctOf(key).pct}
              sx={{ mb: 2, height: 6, borderRadius: 3, bgcolor: '#E2E8F0',
                '& .MuiLinearProgress-bar': { bgcolor: pctOf(key).pct === 100 ? SUCCESS : ph.color } }}/>
            <Paper elevation={0} sx={{ p: 2, bgcolor: '#FAFAFA', border: '1px solid #E2E8F0', borderRadius: 2 }}>
              {Object.entries(ph.items).map(([k, label]) => (
                <FormControlLabel key={k} sx={{ display: 'flex', mb: 0.5 }}
                  control={<Checkbox checked={cl[key]?.items?.[k] || false} color="success"
                    onChange={e => setCl(prev => ({
                      ...prev,
                      [key]: { ...prev[key], items: { ...prev[key]?.items, [k]: e.target.checked } }
                    }))}/>}
                  label={label}/>
              ))}
            </Paper>
          </Box>
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
        <Button variant="contained" onClick={() => { onSave(cl); onClose(); }}>Guardar Checklist</Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── DayListDrawer ─────────────────────────────────────────────────────────────
function DayListDrawer({ open, onClose, surgeries, operatingRooms }) {
  const sorted = [...surgeries].sort((a, b) => (a.startTime || '').localeCompare(b.startTime || ''));
  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: 360, p: 2 } }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5}>
        <Typography variant="h6" fontWeight={800}>Lista quirúrgica del día</Typography>
        <Button size="small" startIcon={<PrintIcon/>} variant="outlined" onClick={() => window.print()}>Imprimir</Button>
      </Stack>
      {sorted.length === 0 && <Alert severity="info">Sin cirugías programadas para este día.</Alert>}
      {sorted.map(surgery => {
        const room       = operatingRooms.find(r => r.id === surgery.operatingRoom);
        const hasAlert   = !surgery.fasting?.confirmed || !surgery.consentSigned;
        const stateCfg   = SURGERY_STATES.find(s => s.value === surgery.status) || {};
        return (
          <Paper key={surgery.id} elevation={0} sx={{
            p: 1.5, mb: 1, border: '1px solid #E2E8F0', borderRadius: 2,
            borderLeft: `4px solid ${stateCfg.color || '#ccc'}`,
          }}>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="caption" color="text.secondary" fontWeight={700}>
                {surgery.startTime} — {surgery.endTime}
              </Typography>
              <Chip label={room?.name || '—'} size="small" sx={{ height: 18, fontSize: '0.65rem' }}/>
            </Stack>
            <Typography variant="body2" fontWeight={800}>{surgery.patientName}</Typography>
            <Typography variant="caption" color="text.secondary" display="block">{surgery.procedureType}</Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              👨‍⚕️ {surgery.mainSurgeon} {surgery.asaScore ? `· ASA ${surgery.asaScore}` : ''}
            </Typography>
            {surgery.patientAlergias && (
              <Chip label={`⚠ ${surgery.patientAlergias}`} size="small" sx={{ bgcolor: DANGER, color: 'white', fontSize: '0.65rem', mt: 0.5 }}/>
            )}
            {hasAlert && (
              <Alert severity="warning" sx={{ py: 0.2, mt: 0.5, fontSize: '0.7rem' }}>
                Pendiente: {!surgery.fasting?.confirmed ? 'ayuno ' : ''}{!surgery.consentSigned ? 'consentimiento' : ''}
              </Alert>
            )}
          </Paper>
        );
      })}
    </Drawer>
  );
}

const SurgeryScheduler = () => {
  const { currentUser, userData } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { activeBranchId, isAllBranches, accessibleBranches, accessibleBranchIds, isAdmin, filterByBranch } = useBranch();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [surgeries, setSurgeries] = useState([]);
  const [patients, setPatients] = useState([]);
  const [owners, setOwners] = useState([]);
  const [professionals, setProfessionals] = useState([]);
  const [procedures, setProcedures] = useState([]); // Procedimientos quirúrgicos desde BD
  const [budgets, setBudgets] = useState([]); // Presupuestos del paciente
  const [allPresupuestos, setAllPresupuestos] = useState([]); // Todos los presupuestos (cargados en mount)
  const [budgetError, setBudgetError] = useState('');
  const [operatingRooms, setOperatingRooms] = useState([]); // Pabellones desde BD
  const [availableTables, setAvailableTables] = useState([]); // Mesas del pabellón seleccionado
  const [openDialog, setOpenDialog] = useState(false);
  const [editingSurgeryId, setEditingSurgeryId] = useState(null); // ID de la cirugía en edición (null = nueva)
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [loading, setLoading] = useState(true);
  const [statusMenuAnchor, setStatusMenuAnchor] = useState(null);
  const [statusMenuSurgery, setStatusMenuSurgery] = useState(null);
  const [draggingSurgeryId, setDraggingSurgeryId] = useState(null);
  const [checklistDlg, setChecklistDlg]  = useState({ open: false, surgery: null });
  const [dayListOpen,  setDayListOpen]   = useState(false);
  const [conflictDlg,  setConflictDlg]  = useState({ open: false, conflicts: [], onConfirm: null });
  const [activeRequestId, setActiveRequestId] = useState(null); // solicitud de derivación en curso
  const [pendingRequestCount, setPendingRequestCount] = useState(0);
  const [latestExams, setLatestExams] = useState({ lab: null, imaging: null });
  const [examDialog, setExamDialog] = useState({ open: false, exam: null });
  const [calendarDialog, setCalendarDialog] = useState({ open: false, date: '', roomId: '', startTime: '09:00' });
  const [calendarSurgeries, setCalendarSurgeries] = useState([]);

  const INIT_FORM = {
    patientId: '', patientName: '', patientAge: '', patientWeight: '',
    patientSpecies: '', patientBreed: '', patientAlergias: '',
    ownerName: '', ownerPhone: '', ownerEmail: '', ownerConvenio: '', budgetNumber: '', presupuestoId: '', presupuestoEstado: '',
    date: '', startTime: '09:00', duration: 60,
    operatingRoom: '', surgicalTable: '',
    priority: 'Electiva',
    asaScore: '', surgicalPosition: '', anesthesiaTechnique: '',
    mainSurgeon: '', mainSurgeonId: '', assistantSurgeon: '', assistantSurgeonId: '', anesthesiologist: '', anesthesiologistId: '', nurse: '',
    procedureType: '', procedureCategory: 'Tejidos Blandos',
    description: '', complications: '',
    fasting: { confirmed: false, hours: 12 },
    preOpExams: {
      hemograma:  { solicitado: false, disponible: false, resultado: '' },
      bioquimico: { solicitado: false, disponible: false, resultado: '' },
      rayosX:     { solicitado: false, disponible: false, resultado: '' },
      ecografia:  { solicitado: false, disponible: false, resultado: '' },
      ecg:        { solicitado: false, disponible: false, resultado: '' },
    },
    anesthesiaProtocol: { premedicacion: '', induccion: '', mantenimiento: '', analgesia: '', farmacosEvitar: '' },
    consentSigned: false, specialNotes: '', equipment: '', materials: '',
    estimatedCost: '', recoveryDays: 7, hospitalizationDays: 1, status: 'programada',
    branchId: '',
  };

  // Estados del formulario
  const [formData, setFormData] = useState(INIT_FORM);

  // Normaliza un documento de 'presupuestos' al shape que consume el dropdown
  const mapPresupuesto = useCallback((b) => ({
    id: b.id,
    presupuestoId: b.id,
    number: b.numero || '',
    amount: b.total || 0,
    estado: b.estado || 'borrador',
    convenioName: b.convenioName || '',
    procedureName: (b.items && b.items[0] && (b.items[0].prestacion || b.items[0].descripcion)) || '',
    procedureId: b.procedureId || '',
  }), []);

  /**
   * Carga los presupuestos de un paciente directamente desde Firestore.
   * Se consulta en cada apertura del formulario para evitar dos problemas:
   *  - Arrastrar los presupuestos del paciente anterior (contaminacion de estado).
   *  - Mostrar un snapshot obsoleto si el presupuesto se creo/pago en otra pantalla.
   * Devuelve el arreglo normalizado y ademas actualiza el estado 'budgets'.
   */
  const loadPatientBudgets = useCallback(async (patientId) => {
    if (!patientId) {
      setBudgets([]);
      setBudgetError('');
      return [];
    }
    try {
      const snap = await getDocs(
        query(collection(db, 'presupuestos'), where('patientId', '==', patientId))
      );
      const list = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => {
          const da = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
          const dbb = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
          return dbb - da;
        })
        .map(mapPresupuesto);
      setBudgets(list);
      setBudgetError('');
      return list;
    } catch (error) {
      console.error('[SurgeryScheduler] Error cargando presupuestos del paciente:', error);
      // Fallback al snapshot en memoria si la consulta directa falla
      const fallback = (allPresupuestos || [])
        .filter((b) => b.patientId === patientId)
        .map(mapPresupuesto);
      setBudgets(fallback);
      setBudgetError(fallback.length ? '' : String(error.message || error));
      return fallback;
    }
  }, [allPresupuestos, mapPresupuesto]);

  // Cargar datos iniciales
  useEffect(() => {
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, activeBranchId, filterByBranch]);

  // Precargar datos del paciente si vienen desde navegación
  useEffect(() => {
    const loadPatientFromNavigation = async () => {
      // Desde la lista de trabajo: derivación de cirugía
      if (location.state?.surgeryRequest) {
        const req = location.state.surgeryRequest;
        setActiveRequestId(req.id || null);
        setEditingSurgeryId(null);

        let presupuestoData = null;
        if (req.presupuestoId) {
          try {
            presupuestoData = await getDocument('presupuestos', req.presupuestoId);
          } catch (e) {
            console.error('Error cargando presupuesto de la derivación:', e);
          }
        }

        // Cargar TODOS los presupuestos del paciente (no solo el de la derivación)
        const loaded = await loadPatientBudgets(req.patientId);
        // Si la derivación trae un presupuesto que la consulta no devolvió, agregarlo
        if (presupuestoData && !loaded.some((b) => b.id === presupuestoData.id)) {
          setBudgets((prev) => [mapPresupuesto(presupuestoData), ...prev]);
        }

        const latestWeight = await getLatestWeight(req.patientId);
        await loadLatestExams(req.patientId);
        setFormData(prev => ({
          ...prev,
          ...requestToSurgeryForm(req),
          date: toLocalDateStr(selectedDate),
          branchId: req.branchId || (!isAllBranches ? activeBranchId : ''),
          budgetNumber: presupuestoData ? presupuestoData.numero : prev.budgetNumber,
          presupuestoId: presupuestoData ? presupuestoData.id : prev.presupuestoId,
          presupuestoEstado: presupuestoData ? (presupuestoData.estado || 'borrador') : prev.presupuestoEstado,
          estimatedCost: presupuestoData ? presupuestoData.total : prev.estimatedCost,
          patientWeight: latestWeight || req.patientWeight || '',
        }));
        setOpenDialog(true);
        return;
      }
      if (location.state?.patientData && location.state?.ownerData) {
        const patient = location.state.patientData;
        const owner = location.state.ownerData;
        const latestWeight = await getLatestWeight(patient.id);
        await loadLatestExams(patient.id);

        // Cargar presupuestos frescos del paciente
        await loadPatientBudgets(patient.id);

        setFormData(prev => ({
          ...prev,
          patientId: patient.id || '',
          patientName: patient.name || '',
          patientAge: patient.age || patient.edad || '',
          patientWeight: latestWeight || patient.weight || '',
          patientSpecies: patient.species || '',
          patientBreed: patient.breed || '',
          ownerName: owner?.name || owner?.fullName || '',
          ownerPhone: owner?.celular || owner?.phone || '',
          ownerEmail: owner?.contact || owner?.email || '',
          ownerConvenio: (owner?.convenio || owner?.convenioName || owner?.convenioId || '') || ''
        }));
        setOpenDialog(true);
      }
    };
    
    loadPatientFromNavigation();
  }, [location.state]);

  // Cargar cirugías del día seleccionado en el calendario picker
  useEffect(() => {
    if (!calendarDialog.open || !calendarDialog.date) {
      setCalendarSurgeries([]);
      return;
    }
    const loadCalendarSurgeries = async () => {
      try {
        const data = await getCollection('surgeries');
        const branchFiltered = filterByBranch(data, { includeUnassigned: true });
        const filtered = branchFiltered.filter(s => s.date === calendarDialog.date);
        setCalendarSurgeries(filtered);
      } catch (error) {
        console.error('Error cargando cirugías del calendario:', error);
        setCalendarSurgeries([]);
      }
    };
    loadCalendarSurgeries();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calendarDialog.open, calendarDialog.date]);

  // Evitar que surgicalTable quede fuera de rango si las mesas disponibles cambian
  useEffect(() => {
    setFormData(prev => {
      if (prev.surgicalTable && !availableTables.includes(prev.surgicalTable)) {
        return { ...prev, surgicalTable: '' };
      }
      return prev;
    });
  }, [availableTables]);

  const loadData = async () => {
    try {
      setLoading(true);
      const dateStr = toLocalDateStr(selectedDate);

      // Cargar en paralelo los datos necesarios para la grilla
      const [surgeriesData, patientsData, ownersData, professionalsData, proceduresData, capacitiesData, presupuestosData] =
        await Promise.all([
          getCollection('surgeries'),
          getCollection('patients'),
          getCollection('owners'),
          getCollection('professionals'),
          getCollection('surgicalProcedures'),
          getCollection('capacities'),
          getCollection('presupuestos'),
        ]);

      // Guardar presupuestos para el dropdown del formulario
      setAllPresupuestos(presupuestosData || []);

      // Filtrar cirugías del día y sucursal activa
      const branchFiltered = filterByBranch(surgeriesData, { includeUnassigned: true });
      const filtered = branchFiltered.filter(s => {
        if (!s.date) return false;
        const surgeryDate = s.date.split('T')[0];
        return surgeryDate === dateStr;
      });

      const patientsMap = Object.fromEntries(patientsData.map(p => [p.id, p]));
      const ownersMap = Object.fromEntries(ownersData.map(o => [o.id, o]));
      // Indice de presupuestos por ID para mostrar su estado REAL en la tarjeta.
      // La cirugia guarda una copia de presupuestoEstado que queda obsoleta cuando
      // el presupuesto se paga en caja, por eso se prioriza el estado vigente.
      const presupuestosMap = Object.fromEntries((presupuestosData || []).map(p => [p.id, p]));
      const enriched = filtered.map(s => {
        const p = patientsMap[s.patientId];
        const owner = ownersMap[s.ownerId] || (p && ownersMap[p.ownerId]) || {};
        const pres = s.presupuestoId ? presupuestosMap[s.presupuestoId] : null;
        return {
          ...s,
          ownerConvenio: s.ownerConvenio || (owner.convenio || owner.convenioName || owner.convenioId) || '',
          presupuestoEstado: pres ? (pres.estado || 'borrador') : (s.presupuestoEstado || ''),
          budgetNumber: pres ? (pres.numero || s.budgetNumber || '') : (s.budgetNumber || ''),
        };
      });
      setSurgeries(enriched);

      setPatients(filterByBranch(patientsData, { includeUnassigned: true }));
      setOwners(filterByBranch(ownersData, { includeUnassigned: true }));
      setProfessionals(filterByBranch(professionalsData, { includeUnassigned: true }).filter(p => p.active !== false).sort((a, b) =>
        (a.fullName || `${a.nombre || ''} ${a.apellidos || ''}`).trim().localeCompare(
          (b.fullName || `${b.nombre || ''} ${b.apellidos || ''}`).trim(), 'es', { sensitivity: 'base' }
        )
      ));
      setProcedures(filterByBranch(proceduresData, { includeUnassigned: true }).filter(p => p.active !== false));

      const pabellones = capacitiesData
        .filter(c => c.type === 'pabellon' && (isAdmin || !c.branchIds?.length || c.branchIds.some(id => accessibleBranchIds.includes(id))))
        .sort((a, b) => {
          const numA = parseInt(a.number) || 0;
          const numB = parseInt(b.number) || 0;
          return numA - numB;
        })
        .map((pabellon, index) => ({
          id: pabellon.id,
          name: `Pabellón ${pabellon.number}`,
          fullName: `Pabellón ${pabellon.number} - ${pabellon.location}`,
          location: pabellon.location,
          number: pabellon.number,
          resources: pabellon.resources || [],
          branchIds: pabellon.branchIds || [],
          color: PABELLON_COLORS[index % PABELLON_COLORS.length]
        }));
      setOperatingRooms(pabellones);

      setLoading(false); // grilla lista para pintar

      // Contar derivaciones pendientes en segundo plano (no bloquea la UI)
      try {
        const requestsData = await getCollection('surgeryRequests');
        const pendingRequests = filterByBranch(requestsData, { includeUnassigned: true })
          .filter(r => r.status === 'pending');
        setPendingRequestCount(pendingRequests.length);
      } catch (_) {
        setPendingRequestCount(0);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      setSnackbar({ open: true, message: 'Error al cargar datos', severity: 'error' });
      setLoading(false);
    }
  };

  const handlePreviousDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() - 1);
    setSelectedDate(newDate);
  };

  const handleNextDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + 1);
    setSelectedDate(newDate);
  };

  const handleToday = () => {
    setSelectedDate(new Date());
  };

  const handleOpenDialog = () => {
    setEditingSurgeryId(null);
    // Nueva cirugia: partir de cero para no arrastrar los presupuestos del paciente anterior
    setBudgets([]);
    setBudgetError('');
    setFormData({
      ...INIT_FORM,
      date: toLocalDateStr(selectedDate),
      branchId: !isAllBranches ? activeBranchId : '',
    });
    setOpenDialog(true);
  };

  const handleEditSurgery = async (surgery) => {
    if (!surgery) return;

    setEditingSurgeryId(surgery.id || null);

    // Descartar los presupuestos del paciente anterior antes de cargar los de esta cirugia
    setBudgets([]);
    setBudgetError('');

    const normalizedDate = surgery.date
      ? surgery.date.split('T')[0]
      : toLocalDateStr(selectedDate);

    const fasting = surgery.fasting || {};
    const preOpExams = surgery.preOpExams || {};

    const selectedRoom = operatingRooms.find(r => r.id === surgery.operatingRoom);
    const resources = selectedRoom?.resources || [];
    const surgicalTable = resources.includes(surgery.surgicalTable) ? surgery.surgicalTable : '';
    setAvailableTables(resources);

    setFormData({
      ...INIT_FORM,
      patientId: surgery.patientId || '',
      patientName: surgery.patientName || '',
      patientAge: surgery.patientAge || surgery.edad || '',
      patientWeight: surgery.patientWeight || '',
      patientSpecies: surgery.patientSpecies || surgery.species || '',
      patientBreed: surgery.patientBreed || surgery.breed || '',
      patientAlergias: surgery.patientAlergias || '',
      ownerName: surgery.ownerName || '',
      ownerPhone: surgery.ownerPhone || '',
      ownerEmail: surgery.ownerEmail || '',
      ownerConvenio: surgery.ownerConvenio || '',
      budgetNumber: surgery.budgetNumber || '',
      presupuestoId: surgery.presupuestoId || '',
      presupuestoEstado: surgery.presupuestoEstado || '',
      asaScore: surgery.asaScore || '',
      surgicalPosition: surgery.surgicalPosition || '',
      anesthesiaTechnique: surgery.anesthesiaTechnique || '',
      date: normalizedDate,
      startTime: surgery.startTime || '09:00',
      duration: surgery.duration || 60,
      operatingRoom: surgery.operatingRoom || '',
      surgicalTable,
      priority: surgery.priority || 'Electiva',
      mainSurgeon: surgery.mainSurgeon || '',
      mainSurgeonId: surgery.mainSurgeonId || '',
      assistantSurgeon: surgery.assistantSurgeon || '',
      assistantSurgeonId: surgery.assistantSurgeonId || '',
      anesthesiologist: surgery.anesthesiologist || '',
      anesthesiologistId: surgery.anesthesiologistId || '',
      nurse: surgery.nurse || '',
      procedureType: surgery.procedureType || '',
      procedureCategory: surgery.procedureCategory || 'Tejidos Blandos',
      description: surgery.description || '',
      complications: surgery.complications || '',
      fasting: {
        confirmed: typeof fasting.confirmed === 'boolean' ? fasting.confirmed : false,
        hours: typeof fasting.hours === 'number' ? fasting.hours : 12,
      },
      preOpExams: {
        hemograma: !!preOpExams.hemograma,
        bioquimico: !!preOpExams.bioquimico,
        rayosX: !!preOpExams.rayosX,
        ecografia: !!preOpExams.ecografia,
        ecg: !!preOpExams.ecg,
      },
      consentSigned: !!surgery.consentSigned,
      specialNotes: surgery.specialNotes || '',
      equipment: surgery.equipment || '',
      materials: surgery.materials || '',
      estimatedCost: surgery.estimatedCost || '',
      recoveryDays: typeof surgery.recoveryDays === 'number' ? surgery.recoveryDays : 7,
      hospitalizationDays: typeof surgery.hospitalizationDays === 'number' ? surgery.hospitalizationDays : 1,
      status: surgery.status || 'programada',
      branchId: surgery.branchId || '',
    });

    setOpenDialog(true);

    // Cargar los presupuestos del paciente de ESTA cirugia y refrescar el estado real
    // del presupuesto vinculado (puede haberse pagado despues de programar la cirugia).
    if (surgery.patientId) {
      const list = await loadPatientBudgets(surgery.patientId);
      const vinculado = surgery.presupuestoId
        ? list.find((b) => b.id === surgery.presupuestoId)
        : list.find((b) => b.number && b.number === surgery.budgetNumber);
      if (vinculado) {
        setFormData(prev => ({
          ...prev,
          budgetNumber: vinculado.number,
          presupuestoId: vinculado.presupuestoId,
          presupuestoEstado: vinculado.estado,
        }));
      }
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingSurgeryId(null);
    setActiveRequestId(null);
    setAvailableTables([]);
    setBudgets([]);
    setBudgetError('');
    setFormData(INIT_FORM);
  };

  const handleOpenDialogFromCalendar = (hour, roomId, mins = 0, preferredTable = '') => {
    setEditingSurgeryId(null);
    const startTime = `${hour.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
    const selectedRoom = operatingRooms.find(r => r.id === roomId);
    const date = toLocalDateStr(selectedDate);
    const duration = 60;
    const defaultTable = getDefaultSurgicalTable(roomId, date, startTime, duration, null, preferredTable);

    // Nueva cirugia desde la grilla: limpiar presupuestos del paciente anterior
    setBudgets([]);
    setBudgetError('');

    setFormData({
      ...INIT_FORM,
      branchId: !isAllBranches ? activeBranchId : '',
      date,
      startTime,
      operatingRoom: roomId,
      surgicalTable: defaultTable,
      duration,
    });

    if (selectedRoom && selectedRoom.resources && selectedRoom.resources.length > 0) {
      setAvailableTables(selectedRoom.resources);
    } else {
      setAvailableTables([]);
    }

    setOpenDialog(true);
  };

  const handleOperatingRoomChange = (e) => {
    const roomId = e.target.value;
    const selectedRoom = operatingRooms.find(r => r.id === roomId);
    const resources = (selectedRoom && selectedRoom.resources) ? selectedRoom.resources : [];
    setAvailableTables(resources);

    // Determinar mesa por defecto: Mesa 1, si ocupada Mesa 2, sino la primera libre
    const startTime = formData.startTime || '09:00';
    const duration = Number(formData.duration) || 60;
    const endTime = calculateEndTime(startTime, duration);
    const toMin = (t) => { const [h, m] = (t || '00:00').split(':').map(Number); return h * 60 + m; };
    const newStart = toMin(startTime);
    const newEnd = toMin(endTime);
    const usedTables = new Set(
      surgeries
        .filter(s =>
          s.operatingRoom === roomId &&
          s.date === formData.date &&
          s.id !== editingSurgeryId &&
          s.surgicalTable &&
          toMin(s.startTime) < newEnd &&
          toMin(s.endTime || calculateEndTime(s.startTime, s.duration)) > newStart
        )
        .map(s => s.surgicalTable)
    );
    const preferred = ['Mesa 1', 'Mesa 2']
      .map(name => resources.find(r => r.toLowerCase() === name.toLowerCase() && !usedTables.has(r)))
      .filter(Boolean);
    const defaultTable = preferred[0] || resources.find(r => !usedTables.has(r)) || '';

    setFormData(prev => ({
      ...prev,
      operatingRoom: roomId,
      surgicalTable: defaultTable,
    }));
  };

  const confirmCalendarSelection = () => {
    const { date, roomId, startTime: selectedStart } = calendarDialog;
    if (!date || !roomId || !selectedStart) {
      setSnackbar({ open: true, message: 'Seleccione una fecha, pabellón y hora', severity: 'warning' });
      return;
    }
    const selectedRoom = operatingRooms.find(r => r.id === roomId);
    const resources = (selectedRoom && selectedRoom.resources) ? selectedRoom.resources : [];
    setAvailableTables(resources);

    const duration = Number(formData.duration) || 60;
    const endTime = calculateEndTime(selectedStart, duration);
    const toMin = (t) => { const [h, m] = (t || '00:00').split(':').map(Number); return h * 60 + m; };
    const newStart = toMin(selectedStart);
    const newEnd = toMin(endTime);
    const usedTables = new Set(
      surgeries
        .filter(s =>
          s.operatingRoom === roomId &&
          s.date === date &&
          s.id !== editingSurgeryId &&
          s.surgicalTable &&
          toMin(s.startTime) < newEnd &&
          toMin(s.endTime || calculateEndTime(s.startTime, s.duration)) > newStart
        )
        .map(s => s.surgicalTable)
    );
    const preferred = ['Mesa 1', 'Mesa 2']
      .map(name => resources.find(r => r.toLowerCase() === name.toLowerCase() && !usedTables.has(r)))
      .filter(Boolean);
    const defaultTable = preferred[0] || resources.find(r => !usedTables.has(r)) || '';

    setFormData(prev => ({ ...prev, date, startTime: selectedStart, operatingRoom: roomId, surgicalTable: defaultTable }));
    setCalendarDialog({ open: false, date: '', roomId: '', startTime: '09:00' });
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'fasting.confirmed') {
      setFormData(prev => ({ ...prev, fasting: { ...prev.fasting, confirmed: checked } }));
    } else if (name === 'fasting.hours') {
      setFormData(prev => ({ ...prev, fasting: { ...prev.fasting, hours: parseInt(value) || 0 } }));
    } else if (name.startsWith('anesthesiaProtocol.')) {
      const key = name.split('.')[1];
      setFormData(prev => ({ ...prev, anesthesiaProtocol: { ...prev.anesthesiaProtocol, [key]: value } }));
    } else {
      setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    }
  };

  // Handler para campos del equipo quirúrgico: guarda nombre e ID
  const handleTeamChange = (e) => {
    const { name, value } = e.target;
    const prof = professionals.find(p => p.id === value);
    const label = prof ? (prof.fullName || `${prof.nombre || ''} ${prof.apellidos || ''}`).trim() || prof.email || '—' : '';
    setFormData(prev => ({
      ...prev,
      [name]: label,
      [`${name}Id`]: value,
    }));
  };

  const handleNestedChange = (section, examKey, field, val) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [examKey]: { ...prev[section][examKey], [field]: val },
      },
    }));
  };

  const loadLatestExams = async (patientId) => {
    if (!patientId) { setLatestExams({ lab: null, imaging: null }); return; }
    try {
      const [labSnap, imagingOrderSnap] = await Promise.all([
        getDocs(query(collection(db, 'labResults'), where('patientId', '==', patientId), limit(50))),
        getDocs(query(collection(db, 'imagingOrders'), where('patientId', '==', patientId), limit(50))),
      ]);

      const labDocs = labSnap.docs.map(d => ({ id: d.id, ...d.data() }))
        .filter(d => ['reported', 'approved'].includes(d.status))
        .sort((a, b) => {
          const aDate = a.uploadedAt?.toDate ? a.uploadedAt.toDate() : (a.uploadedAt ? new Date(a.uploadedAt) : 0);
          const bDate = b.uploadedAt?.toDate ? b.uploadedAt.toDate() : (b.uploadedAt ? new Date(b.uploadedAt) : 0);
          return bDate - aDate;
        });
      const lab = labDocs[0] || null;

      const imagingOrderDocs = imagingOrderSnap.docs.map(d => ({ id: d.id, ...d.data() }))
        .filter(d => ['approved', 'reported'].includes(d.status))
        .sort((a, b) => {
          const aDate = a.reportGeneratedAt?.toDate ? a.reportGeneratedAt.toDate() : (a.reportGeneratedAt ? new Date(a.reportGeneratedAt) : 0);
          const bDate = b.reportGeneratedAt?.toDate ? b.reportGeneratedAt.toDate() : (b.reportGeneratedAt ? new Date(b.reportGeneratedAt) : 0);
          return bDate - aDate;
        });
      const order = imagingOrderDocs[0] || null;
      let imaging = null;
      if (order) {
        const examName = order.studies?.[0]?.examTypeName || order.orderNumber || 'Imagen';
        if (order.resultId) {
          const res = await getDocument('imagingResults', order.resultId);
          if (res) imaging = { ...res, order, examName, orderId: order.id };
        } else {
          imaging = { order, examName, orderId: order.id, findings: '', overallImpression: '' };
        }
      }

      setLatestExams({ lab, imaging });
    } catch (error) {
      console.error('Error cargando exámenes preoperatorios:', error);
      setLatestExams({ lab: null, imaging: null });
    }
  };

  const getLatestWeight = async (patientId) => {
    if (!patientId) return null;
    try {
      const q = query(
        collection(db, 'patients', patientId, 'weights'),
        orderBy('date', 'desc'),
        limit(1)
      );
      const snap = await getDocs(q);
      if (!snap.empty) return snap.docs[0].data().weight;
    } catch (error) {
      console.error('Error cargando último peso:', error);
    }
    return null;
  };

  const handlePatientSelect = async (patient) => {
    if (!patient) {
      setBudgets([]);
      setBudgetError('');
      return;
    }

    const patientId = patient.patientId || patient.id;

    // Buscar el owner usando ownerId del paciente (con getDoc de fallback)
    let owner = owners.find(o => o.id === patient.ownerId);
    if ((!owner || !(owner.convenio || owner.convenioName || owner.convenioId)) && patient.ownerId) {
      try {
        const ownerSnap = await getDoc(doc(db, 'owners', patient.ownerId));
        if (ownerSnap.exists()) owner = { id: ownerSnap.id, ...ownerSnap.data() };
      } catch (e) { console.warn('No se pudo cargar owner directamente:', e); }
    }
    const ownerConvenio = (owner?.convenio || owner?.convenioName || owner?.convenioId || '') || (patient.ownerConvenio || '');
    const latestWeight = await getLatestWeight(patientId);
    await loadLatestExams(patientId);

    // Cargar presupuestos frescos del paciente seleccionado
    await loadPatientBudgets(patientId);

    setFormData(prev => ({
      ...prev,
      patientId:       patientId,
      patientName:     patient.name     || '',
      patientAge:      patient.age      || patient.edad || '',
      patientWeight:   latestWeight || patient.weight   || '',
      patientSpecies:  patient.species  || '',
      patientBreed:    patient.breed    || '',
      patientAlergias: patient.alergias || '',
      ownerName:       owner?.name || owner?.fullName || patient.ownerName || '',
      ownerPhone:      owner?.celular   || owner?.phone   || patient.ownerCelular || patient.ownerPhone || '',
      ownerEmail:      owner?.contact   || owner?.email   || patient.ownerMail || patient.ownerContact || '',
      ownerConvenio:   ownerConvenio,
      // Cambiar de paciente invalida cualquier presupuesto previamente elegido
      budgetNumber:     '',
      presupuestoId:    '',
      presupuestoEstado: '',
    }));
  };

  const handleProcedureSelect = (_, value) => {
    if (!value) {
      setFormData(prev => ({ ...prev, procedureType: '', procedureCategory: prev.procedureCategory }));
      return;
    }
    if (typeof value === 'string') {
      const procedure = procedures.find(p => p.name === value);
      if (procedure) {
        setFormData(prev => ({
          ...prev,
          procedureType: procedure.name,
          procedureCategory: procedure.category,
          duration: procedure.duration || prev.duration,
          equipment: Array.isArray(procedure.equipment) ? procedure.equipment.join(', ') : procedure.equipment || '',
          materials: Array.isArray(procedure.materials) ? procedure.materials.join(', ') : procedure.materials || '',
          description: procedure.description || ''
        }));
      } else {
        setFormData(prev => ({ ...prev, procedureType: value }));
      }
      return;
    }
    setFormData(prev => ({
      ...prev,
      procedureType: value.name || '',
      procedureCategory: value.category || prev.procedureCategory,
      duration: value.duration || prev.duration,
      equipment: Array.isArray(value.equipment) ? value.equipment.join(', ') : value.equipment || '',
      materials: Array.isArray(value.materials) ? value.materials.join(', ') : value.materials || '',
      description: value.description || ''
    }));
  };

  const handleProcedureInputChange = (_, value) => {
    setFormData(prev => ({ ...prev, procedureType: value || '' }));
  };

  const handleBudgetSelect = (e) => {
    const budgetId = e.target.value;

    // Si selecciona la opción vacía, desvincular el presupuesto
    if (budgetId === '') {
      setFormData(prev => ({
        ...prev,
        budgetNumber: '',
        presupuestoId: '',
        presupuestoEstado: '',
      }));
      return;
    }

    // Se identifica por ID de documento: el numero puede venir vacio o duplicado
    const budget = budgets.find(b => b.id === budgetId);

    if (budget) {
      const procedure = budget.procedureId ? procedures.find(p => p.id === budget.procedureId) : null;
      setFormData(prev => {
        const baseUpdate = {
          budgetNumber: budget.number,
          presupuestoId: budget.presupuestoId || '',
          presupuestoEstado: budget.estado || '',
          estimatedCost: budget.amount || prev.estimatedCost,
          ownerConvenio: budget.convenioName || prev.ownerConvenio || ''
        };
        if (procedure) {
          return {
            ...prev,
            ...baseUpdate,
            procedureType: procedure.name,
            procedureCategory: procedure.category,
            duration: procedure.duration || prev.duration,
            equipment: Array.isArray(procedure.equipment) ? procedure.equipment.join(', ') : procedure.equipment || '',
            materials: Array.isArray(procedure.materials) ? procedure.materials.join(', ') : procedure.materials || '',
            description: procedure.description || '',
          };
        }
        return { ...prev, ...baseUpdate };
      });
    }
  };

  const calculateEndTime = (startTime, duration) => {
    const [hours, minutes] = startTime.split(':').map(Number);
    const totalMinutes = hours * 60 + minutes + duration;
    const endHours = Math.floor(totalMinutes / 60);
    const endMinutes = totalMinutes % 60;
    return `${endHours.toString().padStart(2, '0')}:${endMinutes.toString().padStart(2, '0')}`;
  };

  const timeToMin = (t) => { const [h, m] = (t || '00:00').split(':').map(Number); return h * 60 + m; };

  const getDefaultSurgicalTable = (roomId, date, startTime, duration, excludeId, preferredTable = '') => {
    const room = operatingRooms.find(r => r.id === roomId);
    const resources = room?.resources || [];
    if (resources.length === 0) return '';

    const newStart = timeToMin(startTime);
    const newEnd = timeToMin(calculateEndTime(startTime, duration));
    const usedTables = new Set(
      surgeries
        .filter(s =>
          s.operatingRoom === roomId &&
          s.date === date &&
          s.id !== excludeId &&
          s.surgicalTable &&
          timeToMin(s.startTime) < newEnd &&
          timeToMin(s.endTime || calculateEndTime(s.startTime, s.duration)) > newStart
        )
        .map(s => s.surgicalTable)
    );

    if (preferredTable && resources.includes(preferredTable) && !usedTables.has(preferredTable)) {
      return preferredTable;
    }
    const preferred = ['Mesa 1', 'Mesa 2']
      .map(name => resources.find(r => r.toLowerCase() === name.toLowerCase() && !usedTables.has(r)))
      .filter(Boolean);
    return preferred[0] || resources.find(r => !usedTables.has(r)) || '';
  };

  const checkScheduleConflict = () => {
    const conflicts = surgeries.filter(s => {
      if (editingSurgeryId && s.id === editingSurgeryId) {
        return false;
      }

      if (s.operatingRoom !== formData.operatingRoom || s.date !== formData.date) {
        return false;
      }
      
      // Verificar solapamiento de horarios
      const existingStart = s.startTime;
      const existingEnd = s.endTime;
      const newStart = formData.startTime;
      const newEnd = calculateEndTime(formData.startTime, formData.duration);
      
      // Convertir a minutos para comparar
      const toMinutes = (time) => {
        const [h, m] = time.split(':').map(Number);
        return h * 60 + m;
      };
      
      const existingStartMin = toMinutes(existingStart);
      const existingEndMin = toMinutes(existingEnd);
      const newStartMin = toMinutes(newStart);
      const newEndMin = toMinutes(newEnd);
      
      // Hay conflicto si los horarios se solapan
      return (newStartMin < existingEndMin && newEndMin > existingStartMin);
    });
    
    return conflicts;
  };

  const handleOpenStatusMenu = (event, surgery) => {
    event.preventDefault();
    event.stopPropagation();
    setStatusMenuAnchor({ mouseX: event.clientX - 2, mouseY: event.clientY - 4 });
    setStatusMenuSurgery(surgery);
  };

  const handleCloseStatusMenu = () => {
    setStatusMenuAnchor(null);
    setStatusMenuSurgery(null);
  };

  const handleChangeSurgeryStatus = async (newStatus) => {
    if (!statusMenuSurgery || !statusMenuSurgery.id) {
      handleCloseStatusMenu();
      return;
    }

    try {
      const existing = surgeries.find((s) => s.id === statusMenuSurgery.id) || statusMenuSurgery;
      const previousHistory = Array.isArray(existing.statusHistory) ? existing.statusHistory : [];

      const statusEntry = {
        status: newStatus,
        timestamp: new Date(),
        user: userData?.name || userData?.email || 'Usuario',
      };

      const updatedData = {
        ...existing,
        status: newStatus,
        statusHistory: [...previousHistory, statusEntry],
        updatedAt: new Date(),
      };

      const { id, ...dataToSave } = updatedData;
      await updateDocument('surgeries', statusMenuSurgery.id, dataToSave);

      // Sincronizar estado con la derivación asociada en surgeryRequests
      const requestStatusMap = {
        completada: 'completed',
        cancelada: 'cancelled',
        pospuesta: 'scheduled',
        programada: 'scheduled',
        confirmada: 'scheduled',
        'pre-operatorio': 'scheduled',
        'en-quirofano': 'scheduled',
        'post-operatorio': 'scheduled',
      };
      const reqStatus = requestStatusMap[newStatus];
      if (existing.surgeryRequestId && reqStatus) {
        try {
          await updateDocument('surgeryRequests', existing.surgeryRequestId, {
            status: reqStatus,
            updatedAt: new Date(),
          });
        } catch (reqErr) {
          console.error('No se pudo sincronizar la derivación:', reqErr);
        }
      }

      setSnackbar({
        open: true,
        message: `Estado actualizado a "${SURGERY_STATES.find((s) => s.value === newStatus)?.label || newStatus}"`,
        severity: 'success',
      });

      handleCloseStatusMenu();
      loadData();
    } catch (error) {
      console.error('❌ Error al actualizar estado de cirugía:', error);
      setSnackbar({
        open: true,
        message: 'Error al actualizar estado de cirugía: ' + error.message,
        severity: 'error',
      });
      handleCloseStatusMenu();
    }
  };

  const handleDropSurgery = async (hour, roomId, preferredTable = '') => {
    if (!draggingSurgeryId) return;

    const surgery = surgeries.find((s) => s.id === draggingSurgeryId);
    if (!surgery) {
      setDraggingSurgeryId(null);
      return;
    }

    const currentHour = parseInt(surgery.startTime.split(':')[0], 10);
    if (surgery.operatingRoom === roomId && currentHour === hour) {
      setDraggingSurgeryId(null);
      return;
    }

    const newStartTime = `${hour.toString().padStart(2, '0')}:00`;
    const duration = surgery.duration || 60;
    const newEndTime = calculateEndTime(newStartTime, duration);
    const keepTable = roomId === surgery.operatingRoom ? surgery.surgicalTable : preferredTable;
    const newSurgicalTable = getDefaultSurgicalTable(roomId, surgery.date, newStartTime, duration, surgery.id, keepTable);

    const updatedData = {
      ...surgery,
      operatingRoom: roomId,
      startTime: newStartTime,
      endTime: newEndTime,
      surgicalTable: newSurgicalTable,
      updatedAt: new Date(),
    };

    const { id, ...dataToSave } = updatedData;

    try {
      await updateDocument('surgeries', surgery.id, dataToSave);
      setSnackbar({
        open: true,
        message: 'Cirugía reprogramada en el calendario',
        severity: 'success',
      });
      setDraggingSurgeryId(null);
      loadData();
    } catch (error) {
      console.error('❌ Error al reprogramar cirugía desde calendario:', error);
      setSnackbar({
        open: true,
        message: 'Error al mover cirugía: ' + error.message,
        severity: 'error',
      });
      setDraggingSurgeryId(null);
    }
  };

  const handleOpenSupplies = (surgery) => {
    const id = surgery.id;
    if (!id) {
      setSnackbar({
        open: true,
        message: 'No se encontró el ID de la cirugía para cargar insumos',
        severity: 'error',
      });
      return;
    }
    navigate(`/surgery-supplies/${id}`, { state: { surgery } });
  };

  const handleOpenOperativeProtocol = (surgery) => {
    const id = surgery.id;
    if (!id) {
      setSnackbar({
        open: true,
        message: 'No se encontró el ID de la cirugía para abrir el protocolo',
        severity: 'error',
      });
      return;
    }
    navigate(`/surgery-protocol/${id}`, { state: { surgery } });
  };

  const handleSaveSurgery = async () => {
    // Validaciones detalladas de campos obligatorios
    if (!formData.operatingRoom) {
      setSnackbar({ open: true, message: '⚠️ Debe seleccionar un pabellón', severity: 'error' });
      return;
    }
    
    if (!formData.patientName) {
      setSnackbar({ open: true, message: 'Debe seleccionar un paciente', severity: 'error' });
      return;
    }
    
    if (!formData.date) {
      setSnackbar({ open: true, message: '⚠️ La fecha de cirugía es obligatoria', severity: 'error' });
      return;
    }
    
    if (!formData.startTime) {
      setSnackbar({ open: true, message: '⚠️ La hora de inicio es obligatoria', severity: 'error' });
      return;
    }
    
    if (!formData.mainSurgeon) {
      setSnackbar({ open: true, message: 'Debe seleccionar un cirujano principal', severity: 'error' });
      return;
    }
    
    if (!formData.procedureType) {
      setSnackbar({ open: true, message: 'Debe seleccionar un procedimiento quirúrgico', severity: 'error' });
      return;
    }
    
    const conflicts = checkScheduleConflict();
    if (conflicts.length > 0) {
      // Obtener nombre legible del pabellón
      const roomName = operatingRooms.find(r => r.id === formData.operatingRoom)?.name || formData.operatingRoom;
      const newEndTime = calculateEndTime(formData.startTime, formData.duration);
      
      const conflictMessages = conflicts.map(c => 
        `• ${c.patientName} - ${c.procedureType} (${c.startTime} a ${c.endTime})`
      ).join('\n');
      
      setSnackbar({ 
        open: true, 
        message: `⚠️ CONFLICTO DE HORARIO DETECTADO\n\nSu cirugía (${formData.startTime} a ${newEndTime}) se solapa con:\n${conflictMessages}\n\nEn: ${roomName}\n\nSe guardará de todas formas. Considere reprogramar.`, 
        severity: 'warning' 
      });
    }

    try {
      const endTime = calculateEndTime(formData.startTime, formData.duration);

      let normalizedDate = formData.date;
      if (formData.date.includes('T')) {
        normalizedDate = formData.date.split('T')[0];
      }

      const branchId = formData.branchId || (!isAllBranches ? activeBranchId : '');
      const baseData = {
        ...formData,
        date: normalizedDate,
        endTime,
        branchId,
      };

      const statusEntry = {
        status: formData.status,
        timestamp: new Date(),
        user: userData?.name || userData?.email || 'Usuario',
      };

      if (editingSurgeryId) {
        const existing = surgeries.find(s => s.id === editingSurgeryId) || {};
        const previousHistory = Array.isArray(existing.statusHistory) ? existing.statusHistory : [];

        const updatedData = {
          ...existing,
          ...baseData,
          statusHistory: [...previousHistory, statusEntry],
          updatedAt: new Date(),
        };

        const { id, ...dataToSave } = updatedData;
        await updateDocument('surgeries', editingSurgeryId, dataToSave);
        setSnackbar({ open: true, message: 'Cirugía actualizada exitosamente', severity: 'success' });
      } else {
        const newData = {
          ...baseData,
          branchId: baseData.branchId || (!isAllBranches ? activeBranchId : ''),
          surgeryRequestId: activeRequestId || null,
          createdAt: new Date(),
          createdBy: userData?.name || userData?.email || 'Usuario',
          createdByEmail: currentUser?.email || '',
          statusHistory: [statusEntry],
        };

        const newSurgeryId = await addDocument('surgeries', newData);
        // Si proviene de una derivación, marcarla como agendada
        if (activeRequestId) {
          try {
            await updateDocument('surgeryRequests', activeRequestId, {
              status: 'scheduled',
              surgeryId: newSurgeryId,
              scheduledDate: normalizedDate,
              updatedAt: new Date(),
            });
          } catch (reqErr) {
            console.error('No se pudo actualizar la derivación:', reqErr);
          }
        }
        setSnackbar({ open: true, message: 'Cirugía programada exitosamente', severity: 'success' });
      }

      handleCloseDialog();
      loadData();
    } catch (error) {
      console.error('❌ Error saving surgery:', error);
      setSnackbar({ open: true, message: 'Error al guardar cirugía: ' + error.message, severity: 'error' });
    }
  };

  const handleCloseSnackbar = () => setSnackbar(p => ({ ...p, open: false }));

  const handleSaveChecklist = useCallback(async (cl) => {
    if (!checklistDlg.surgery?.id) return;
    await updateDocument('surgeries', checklistDlg.surgery.id, {
      surgicalSafetyChecklist: cl, updatedAt: new Date(),
    });
    setSnackbar({ open: true, message: 'Checklist de seguridad guardado', severity: 'success' });
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checklistDlg.surgery]);

  const filteredOperatingRooms = useMemo(() => {
    if (!formData.branchId) return operatingRooms;
    return operatingRooms.filter(r =>
      isAdmin ||
      !r.branchIds?.length ||
      r.branchIds.some(id => id === formData.branchId)
    );
  }, [operatingRooms, formData.branchId, isAdmin]);

  const stats = useMemo(() => ({
    total:    surgeries.length,
    enCurso:  surgeries.filter(s => s.status === 'en-quirofano').length,
    alertas:  surgeries.filter(s => !s.fasting?.confirmed || !s.consentSigned).length,
    ocupPct:  operatingRooms.length === 0 ? 0 : Math.min(100, Math.round(
                surgeries.reduce((acc, s) => acc + (s.duration || 60), 0) /
                (operatingRooms.length * TOTAL_HOURS * 60) * 100
              )),
  }), [surgeries, operatingRooms]);

  // Función para obtener TODAS las cirugías de un pabellón y hora específica
  const getSurgeriesAtTimeAndRoom = (hour, roomId) => {
    return surgeries.filter(surgery => {
      const surgeryHour = parseInt(surgery.startTime.split(':')[0]);
      const surgeryEndHour = parseInt(surgery.endTime.split(':')[0]);
      const surgeryEndMinute = parseInt(surgery.endTime.split(':')[1]);
      
      return surgery.operatingRoom === roomId && 
             hour >= surgeryHour && 
             hour < surgeryEndHour + (surgeryEndMinute > 0 ? 1 : 0);
    });
  };

  const getStateColor = (status) => {
    const state = SURGERY_STATES.find(s => s.value === status);
    return state ? state.color : '#9e9e9e';
  };

  return (
    <Box sx={{ bgcolor: '#F4F6F8', minHeight: '100vh', pb: 4 }}
      onMouseUp={e => { if (e.button !== 0) return; setDraggingSurgeryId(null); }}>
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <Paper elevation={0} sx={{ px: 3, pt: 2, pb: 1.5, borderBottom: '2px solid #E2E8F0', mb: 2 }}>
        {/* Row 1 */}
        <Stack direction="row" alignItems="center" spacing={2} mb={1.5}>
          <LocalHospital sx={{ color: PRIMARY, fontSize: 28 }}/>
          <Typography variant="h5" fontWeight={800} color={PRIMARY} sx={{ flex: 1 }}>
            Programación Quirúrgica
          </Typography>

          {/* Date nav */}
          <IconButton onClick={handlePreviousDay}><ChevronLeft/></IconButton>
          <Button onClick={handleToday} startIcon={<CalendarToday/>} variant="outlined" size="small">Hoy</Button>
          <Typography variant="subtitle1" fontWeight={700} sx={{ minWidth: 260, textAlign: 'center' }}>
            {selectedDate.toLocaleDateString('es-CL', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </Typography>
          <IconButton onClick={handleNextDay}><ChevronRight/></IconButton>

          <Tooltip title="Lista del día">
            <IconButton onClick={() => setDayListOpen(true)}><ViewListIcon/></IconButton>
          </Tooltip>
          <Button variant="outlined" onClick={() => navigate('/surgery/worklist')}
            startIcon={
              <Badge badgeContent={pendingRequestCount} color="error">
                <ChecklistIcon />
              </Badge>
            }
            sx={{ fontWeight: 700, borderColor: '#CBD5E1', color: PRIMARY }}>
            Lista de trabajo
          </Button>
          <Button variant="contained" startIcon={<AddIcon/>} onClick={handleOpenDialog}
            sx={{ bgcolor: PRIMARY, fontWeight: 700, '&:hover': { bgcolor: '#122a47' } }}>
            Programar Cirugía
          </Button>
        </Stack>

        {/* Row 2 — KPIs */}
        <Stack direction="row" spacing={2}>
          {[
            { label: 'Cirugías hoy', value: stats.total, color: PRIMARY },
            { label: 'En quirófano ahora', value: stats.enCurso, color: stats.enCurso > 0 ? DANGER : 'text.secondary' },
            { label: 'Ocupación pabellones', value: `${stats.ocupPct}%`, color: stats.ocupPct > 80 ? DANGER : stats.ocupPct > 50 ? AMBER : SUCCESS },
            { label: 'Alertas pendientes', value: stats.alertas, color: stats.alertas > 0 ? AMBER : SUCCESS },
          ].map(({ label, value, color }) => (
            <Paper key={label} elevation={0} sx={{ px: 2, py: 1, border: '1px solid #E2E8F0', borderRadius: 2, minWidth: 130 }}>
              <Typography variant="h5" fontWeight={800} color={color}>{value}</Typography>
              <Typography variant="caption" color="text.secondary">{label}</Typography>
            </Paper>
          ))}
        </Stack>
      </Paper>
      {/* ── Proportional Grid ─────────────────────────────────────────── */}
      <Box sx={{ px: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1.5, py: 8 }}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary">Cargando agenda de pabellones…</Typography>
          </Box>
        ) : operatingRooms.length === 0 ? (
          <Alert severity="warning" sx={{ m: 2 }}>
            No hay pabellones configurados. Vaya a <strong>Configuración › Capacidades</strong> para agregar pabellones.
          </Alert>
        ) : (
          <Paper elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: 2, overflow: 'auto' }}>
            {/* Room header */}
            <Box sx={{ display: 'grid',
              gridTemplateColumns: `72px ${operatingRooms.map(r => `repeat(${Math.max(1, r.resources?.length || 1)}, 1fr)`).join(' ')}`,
              bgcolor: PRIMARY, color: 'white', position: 'sticky', top: 0, zIndex: 5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 1 }}>
                <AccessTime fontSize="small"/>
              </Box>
              {operatingRooms.map((room) => {
                const tableCount = Math.max(1, room.resources?.length || 1);
                return (
                  <Box key={room.id} sx={{ p: 1, textAlign: 'center', borderLeft: '1px solid rgba(255,255,255,0.2)',
                    gridColumn: `span ${tableCount}` }}>
                    <Typography variant="subtitle2" fontWeight={800}>{room.name}</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>{room.location}</Typography>
                    <Box sx={{ width: '60%', mx: 'auto', mt: 0.5, height: 4, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.2)',
                      overflow: 'hidden' }}>
                      <Box sx={{ height: '100%', width: `${Math.min(100, Math.round(
                        surgeries.filter(s => s.operatingRoom === room.id).reduce((a, s) => a + (s.duration||60), 0) / (TOTAL_HOURS * 60) * 100
                      ))}%`, bgcolor: TEAL }}/>
                    </Box>
                  </Box>
                );
              })}
            </Box>

            {/* Absolute-positioned grid body */}
            <Box sx={{ display: 'grid',
              gridTemplateColumns: `72px ${operatingRooms.map(r => `repeat(${Math.max(1, r.resources?.length || 1)}, 1fr)`).join(' ')}`,
              position: 'relative', minHeight: TOTAL_HOURS * ROW_HEIGHT }}>

              {/* Hour lines */}
              {WORK_HOURS.map(hour => (
                <Box key={hour} sx={{ gridColumn: '1 / -1', position: 'absolute',
                  top: (hour - FIRST_HOUR) * ROW_HEIGHT, left: 0, right: 0, height: ROW_HEIGHT,
                  borderTop: `1px solid ${hour % 2 === 0 ? '#E2E8F0' : '#F1F5F9'}`,
                  pointerEvents: 'none' }}>
                  <Typography variant="caption" sx={{ position: 'absolute', left: 6, top: 3,
                    color: 'text.secondary', fontWeight: 700, fontSize: '0.7rem' }}>
                    {`${hour.toString().padStart(2, '0')}:00`}
                  </Typography>
                  {/* 30-min dashed line */}
                  <Box sx={{ position: 'absolute', top: '50%', left: 72, right: 0,
                    borderTop: '1px dashed #F1F5F9', pointerEvents: 'none' }}/>
                </Box>
              ))}

              {/* Time column spacer */}
              <Box sx={{ gridColumn: 1, bgcolor: '#FAFAFA', borderRight: '1px solid #E2E8F0' }}/>

              {/* Pabellon columns (una sub-columna por mesa) */}
              {operatingRooms.map((room) => {
                const roomSurgeries = surgeries.filter(s => s.operatingRoom === room.id);
                const tableCount = Math.max(1, room.resources?.length || 1);

                // Asignar sub-columna (mesa) para cada cirugía de este pabellón
                const sorted = [...roomSurgeries].sort((a, b) => (a.startTime || '').localeCompare(b.startTime || ''));
                const used = Array.from({ length: tableCount }, () => []);
                const tableAssignments = {};
                sorted.forEach((s) => {
                  const start = timeToMin(s.startTime);
                  const end = timeToMin(s.endTime || calculateEndTime(s.startTime, s.duration));
                  let preferred = -1;
                  if (s.surgicalTable && room.resources.includes(s.surgicalTable)) {
                    preferred = room.resources.indexOf(s.surgicalTable);
                    const conflict = used[preferred].some(iv => start < iv.end && end > iv.start);
                    if (!conflict) {
                      tableAssignments[s.id] = preferred;
                      used[preferred].push({ start, end });
                      return;
                    }
                  }
                  for (let i = 0; i < tableCount; i++) {
                    const conflict = used[i].some(iv => start < iv.end && end > iv.start);
                    if (!conflict) {
                      tableAssignments[s.id] = i;
                      used[i].push({ start, end });
                      return;
                    }
                  }
                  tableAssignments[s.id] = preferred >= 0 ? preferred : 0;
                  used[tableAssignments[s.id]].push({ start, end });
                });

                return Array.from({ length: tableCount }, (_, tableIdx) => {
                  const preferredTable = room.resources?.[tableIdx] || '';
                  const subSurgeries = roomSurgeries.filter(s => tableAssignments[s.id] === tableIdx);
                  return (
                    <Box key={`${room.id}-${tableIdx}`} sx={{ gridColumn: 'auto', position: 'relative',
                      borderLeft: tableIdx === 0 ? '1px solid #E2E8F0' : '1px dashed #E2E8F0', cursor: 'cell' }}
                      onClick={e => {
                        if (draggingSurgeryId) return;
                        const rect = e.currentTarget.getBoundingClientRect();
                        const relY  = e.clientY - rect.top;
                        const hour  = FIRST_HOUR + Math.floor(relY / ROW_HEIGHT);
                        const mins  = relY % ROW_HEIGHT >= ROW_HEIGHT / 2 ? 30 : 0;
                        handleOpenDialogFromCalendar(hour, room.id, mins, preferredTable);
                      }}
                      onMouseUp={e => {
                        if (!draggingSurgeryId || e.button !== 0) return;
                        e.stopPropagation();
                        const rect = e.currentTarget.getBoundingClientRect();
                        const relY = e.clientY - rect.top;
                        const hour = FIRST_HOUR + Math.floor(relY / ROW_HEIGHT);
                        handleDropSurgery(hour, room.id, preferredTable);
                      }}>
                      {subSurgeries.map(surgery => (
                        <SurgeryCard key={surgery.id} surgery={surgery}
                          roomName={room.name}
                          tableIndex={0}
                          tableCount={1}
                          hasAlert={!surgery.fasting?.confirmed || !surgery.consentSigned}
                          isRunning={surgery.status === 'en-quirofano'}
                          draggingSurgeryId={draggingSurgeryId}
                          setDraggingSurgeryId={setDraggingSurgeryId}
                          onEdit={handleEditSurgery}
                          onContextMenu={handleOpenStatusMenu}
                          onSupplies={handleOpenSupplies}
                          onProtocol={handleOpenOperativeProtocol}
                          onRecord={s => navigate(`/patients/${s.patientId}/record`)}
                          onChecklist={s => setChecklistDlg({ open: true, surgery: s })}
                        />
                      ))}
                    </Box>
                  );
                });
              })}
            </Box>
          </Paper>
        )}
      </Box>
      {/* Surgery Dialog - Será el formulario completo */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ textAlign: 'left' }}>Programar Cirugía</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={3}>
              {/* Sección 1: Programación */}
              <Grid size={12}>
                <Typography variant="h6" gutterBottom color="primary" align="left">
                  📅 Programación
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid size={12}>
                <Button
                  variant="outlined"
                  startIcon={<CalendarMonthIcon />}
                  onClick={() => setCalendarDialog({
                    open: true,
                    date: formData.date || toLocalDateStr(selectedDate),
                    roomId: formData.operatingRoom || '',
                    startTime: formData.startTime || '09:00'
                  })}
                  sx={{ mb: 2 }}
                >
                  Ver calendario
                </Button>
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  sm: 6
                }}>
                <TextField
                  select
                  label="Pabellón *"
                  name="operatingRoom"
                  value={formData.operatingRoom}
                  onChange={handleOperatingRoomChange}
                  fullWidth
                  required
                  error={!formData.operatingRoom}
                  helperText={
                    !formData.operatingRoom
                      ? 'Campo obligatorio'
                      : operatingRooms.length === 0
                        ? 'Configure pabellones en Configuración > Capacidades'
                        : ''
                  }
                >
                  {operatingRooms.map((room) => (
                    <MenuItem key={room.id} value={room.id}>
                      {room.name} - {room.location}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  sm: 6
                }}>
                <TextField
                  select
                  label="Mesa Quirúrgica"
                  name="surgicalTable"
                  value={formData.surgicalTable}
                  onChange={handleInputChange}
                  fullWidth
                  disabled={!formData.operatingRoom || availableTables.length === 0}
                  helperText={
                    !formData.operatingRoom
                      ? 'Seleccione un pabellón primero'
                      : availableTables.length === 0
                        ? 'Este pabellón no tiene mesas configuradas'
                        : 'Opcional'
                  }
                >
                  <MenuItem value="">Sin especificar</MenuItem>
                  {availableTables.map((table, index) => (
                    <MenuItem key={index} value={table}>
                      {table}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              {/* Sección 1: Información del Paciente */}
              <Grid size={12}>
                <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 2 }} align="left">
                  📋 Información del Paciente
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  sm: 6
                }}>
                <TextField
                  label="Nombre del Paciente *"
                  name="patientName"
                  value={formData.patientName}
                  onChange={handleInputChange}
                  fullWidth
                  required
                />
              </Grid>

              <Grid
                size={{
                  xs: 6,
                  sm: 3
                }}>
                <TextField
                  label="Edad"
                  name="patientAge"
                  value={formData.patientAge}
                  onChange={handleInputChange}
                  fullWidth
                  placeholder="Ej: 5 años"
                />
              </Grid>

              <Grid
                size={{
                  xs: 6,
                  sm: 3
                }}>
                <TextField
                  label="Peso (kg) *"
                  name="patientWeight"
                  value={formData.patientWeight}
                  onChange={handleInputChange}
                  fullWidth
                  type="number"
                  required
                  helperText="Importante para anestesia"
                />
              </Grid>

              <Grid size={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    Seleccionar Paciente en la base de datos
                  </Typography>
                  <Autocomplete
                    options={accessibleBranches}
                    value={accessibleBranches.find((b) => b.id === formData.branchId) || null}
                    onChange={(e, value) => setFormData(prev => ({ ...prev, branchId: value?.id || '' }))}
                    getOptionLabel={(b) => b.name}
                    isOptionEqualToValue={(o, v) => o.id === v.id}
                    renderInput={(params) => <TextField {...params} size="small" label="Sucursal" placeholder="Seleccionar sucursal" />}
                    noOptionsText="Sin sucursales"
                    sx={{ minWidth: 220, mr: 1 }}
                  />
                  {formData.patientId && (
                    <IconButton size="small" onClick={() => { setFormData(prev => ({ ...prev, patientId: '', patientName: '', patientAge: '', patientWeight: '', patientSpecies: '', patientBreed: '', patientAlergias: '', ownerName: '', ownerPhone: '', ownerEmail: '', ownerConvenio: '' })); setBudgets([]); }}>
                      <Close fontSize="small" />
                    </IconButton>
                  )}
                </Box>

                {!formData.patientId ? (
                  <PatientSearch
                    onPatientSelect={handlePatientSelect}
                    showFilters={false}
                    label="Buscar paciente"
                    patients={patients}
                    owners={owners}
                  />
                ) : (
                  <Box sx={{ p: 2, bgcolor: '#F0FDF4', borderRadius: 2, border: '1px solid #BBF7D0' }}>
                    <Typography variant="body2" fontWeight={700}>{formData.patientName}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formData.patientSpecies} · {formData.patientBreed} · {formData.patientAge}
                    </Typography>
                    {formData.ownerName && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        Propietario: {formData.ownerName}
                      </Typography>
                    )}
                  </Box>
                )}
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  Opcional: busca y precarga datos desde pacientes registrados
                </Typography>
              </Grid>

              <Grid
                size={{
                  xs: 6,
                  sm: 3
                }}>
                <TextField
                  label="Especie"
                  name="patientSpecies"
                  value={formData.patientSpecies}
                  onChange={handleInputChange}
                  fullWidth
                />
              </Grid>

              <Grid
                size={{
                  xs: 6,
                  sm: 3
                }}>
                <TextField
                  label="Raza"
                  name="patientBreed"
                  value={formData.patientBreed}
                  onChange={handleInputChange}
                  fullWidth
                />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  sm: 4
                }}>
                <TextField
                  label="Nombre del Tutor"
                  name="ownerName"
                  value={formData.ownerName}
                  onChange={handleInputChange}
                  fullWidth
                />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  sm: 4
                }}>
                <TextField
                  label="Teléfono del Tutor"
                  name="ownerPhone"
                  value={formData.ownerPhone}
                  onChange={handleInputChange}
                  fullWidth
                />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  sm: 4
                }}>
                <TextField
                  label="Email del Tutor"
                  name="ownerEmail"
                  value={formData.ownerEmail}
                  onChange={handleInputChange}
                  fullWidth
                  type="email"
                />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  sm: 4
                }}>
                <TextField
                  label="Convenio"
                  name="ownerConvenio"
                  value={formData.ownerConvenio}
                  onChange={handleInputChange}
                  fullWidth
                  helperText="Convenio del tutor"
                />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  sm: 4
                }}>
                {budgets.length > 0 ? (
                  <TextField
                    select
                    label="N° de Presupuesto"
                    // El value es el ID del documento: el numero puede venir vacio o duplicado
                    value={budgets.some(b => b.id === formData.presupuestoId) ? formData.presupuestoId : ''}
                    onChange={handleBudgetSelect}
                    fullWidth
                    helperText={
                      formData.budgetNumber && !formData.presupuestoId
                        ? `N° ingresado manualmente: ${formData.budgetNumber}`
                        : `${budgets.length} presupuesto${budgets.length !== 1 ? 's' : ''} de ${formData.patientName || 'este paciente'}`
                    }
                  >
                    <MenuItem value="">Sin presupuesto vinculado</MenuItem>
                    {budgets.map((budget) => (
                      <MenuItem key={budget.id} value={budget.id}>
                        {budget.number || '(sin n°)'} - $
                        {budget.amount?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
                        {budget.procedureName && ` - ${budget.procedureName}`}
                        {budget.estado && ` · ${budget.estado}`}
                      </MenuItem>
                    ))}
                  </TextField>
                ) : (
                  <Box>
                    <TextField
                      label="N° de Presupuesto"
                      name="budgetNumber"
                      value={formData.budgetNumber}
                      onChange={handleInputChange}
                      fullWidth
                      placeholder="Ej: PRES-2025-001"
                      disabled={!formData.patientId}
                      helperText={
                        !formData.patientId
                          ? 'Seleccione un paciente primero'
                          : 'Este paciente no tiene presupuestos: puede escribir el N° manualmente'
                      }
                    />
                    {budgetError && (
                      <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                        Error cargando presupuestos: {budgetError}
                      </Typography>
                    )}
                  </Box>
                )}
                {formData.presupuestoId && (
                  <Button
                    size="small"
                    variant="outlined"
                    sx={{ mt: 1 }}
                    onClick={() => navigate(`/presupuestos/${formData.presupuestoId}`)}
                  >
                    Ver / editar presupuesto
                  </Button>
                )}
              </Grid>

              {/* Sección 2: Horario y duración */}
              <Grid size={12}>
                <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 2 }} align="left">
                  🕒 Horario y Duración
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  sm: 3
                }}>
                <TextField
                  label="Fecha *"
                  name="date"
                  type="date"
                  value={formData.date}
                  onChange={handleInputChange}
                  fullWidth
                  required
                  error={!formData.date}
                  helperText={!formData.date ? 'Campo obligatorio' : ''}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  sm: 3
                }}>
                <TextField
                  label="Hora Inicio *"
                  name="startTime"
                  type="time"
                  value={formData.startTime}
                  onChange={handleInputChange}
                  fullWidth
                  required
                  error={!formData.startTime}
                  helperText={!formData.startTime ? 'Campo obligatorio' : ''}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  sm: 3
                }}>
                <TextField
                  select
                  label="Duración Estimada *"
                  name="duration"
                  value={formData.duration}
                  onChange={handleInputChange}
                  fullWidth
                  required
                >
                  <MenuItem value={30}>30 minutos</MenuItem>
                  <MenuItem value={60}>1 hora</MenuItem>
                  <MenuItem value={90}>1.5 horas</MenuItem>
                  <MenuItem value={120}>2 horas</MenuItem>
                  <MenuItem value={180}>3 horas</MenuItem>
                  <MenuItem value={240}>4 horas</MenuItem>
                </TextField>
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  sm: 4
                }}>
                <TextField
                  select
                  label="Prioridad *"
                  name="priority"
                  value={formData.priority}
                  onChange={handleInputChange}
                  fullWidth
                  required
                >
                  <MenuItem value="Electiva">Electiva</MenuItem>
                  <MenuItem value="Urgente">Urgente</MenuItem>
                  <MenuItem value="Emergencia">Emergencia</MenuItem>
                </TextField>
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  sm: 4
                }}>
                <TextField
                  select
                  label="Estado *"
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  fullWidth
                  required
                >
                  {SURGERY_STATES.map((state) => (
                    <MenuItem key={state.value} value={state.value}>
                      {state.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              {/* ─── Sección Pre-anestésica ─── */}
              <Grid size={12}>
                <Typography variant="h6" color="primary" sx={{ mt: 2 }} align="left">
                  🩺 Evaluación Pre-anestésica
                </Typography>
                <Divider sx={{ mb: 2 }}/>
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  sm: 4
                }}>
                <TextField select label="ASA Score *" name="asaScore"
                  value={formData.asaScore} onChange={handleInputChange} fullWidth required
                  error={!formData.asaScore} helperText={!formData.asaScore ? 'Obligatorio' : ''}>
                  {ASA_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                </TextField>
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  sm: 4
                }}>
                <TextField select label="Posición quirúrgica" name="surgicalPosition"
                  value={formData.surgicalPosition} onChange={handleInputChange} fullWidth>
                  <MenuItem value="">No especificada</MenuItem>
                  <MenuItem value="dorsal">Decúbito dorsal</MenuItem>
                  <MenuItem value="lateral_der">Lateral derecho</MenuItem>
                  <MenuItem value="lateral_izq">Lateral izquierdo</MenuItem>
                  <MenuItem value="ventral">Decúbito ventral</MenuItem>
                  <MenuItem value="oblicuo">Oblicuo</MenuItem>
                  <MenuItem value="otro">Otro</MenuItem>
                </TextField>
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  sm: 4
                }}>
                <TextField select label="Técnica de anestesia" name="anesthesiaTechnique"
                  value={formData.anesthesiaTechnique} onChange={handleInputChange} fullWidth>
                  <MenuItem value="">No especificada</MenuItem>
                  <MenuItem value="general_inhalatoria">General inhalatoria</MenuItem>
                  <MenuItem value="tiva">TIVA (intravenosa total)</MenuItem>
                  <MenuItem value="locoregional">Locoregional / epidural</MenuItem>
                  <MenuItem value="sedacion">Sedación profunda</MenuItem>
                  <MenuItem value="combinada">Combinada</MenuItem>
                </TextField>
              </Grid>

              {formData.patientAlergias && (
                <Grid size={12}>
                  <Alert severity="error" icon={<WarningIcon/>}>
                    <Typography variant="subtitle2">⚠ Alergias registradas del paciente:</Typography>
                    <Typography variant="body2" fontWeight={700}>{formData.patientAlergias}</Typography>
                  </Alert>
                </Grid>
              )}

              <Grid
                size={{
                  xs: 12,
                  sm: 6
                }}>
                <TextField label="Premedicación planeada" name="anesthesiaProtocol.premedicacion"
                  value={formData.anesthesiaProtocol?.premedicacion || ''}
                  onChange={handleInputChange} fullWidth multiline rows={2}
                  placeholder="Ej: Acepromazina 0.05 mg/kg IM + Butorfanol 0.2 mg/kg IM"/>
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 6
                }}>
                <TextField label="Inducción planeada" name="anesthesiaProtocol.induccion"
                  value={formData.anesthesiaProtocol?.induccion || ''}
                  onChange={handleInputChange} fullWidth multiline rows={2}
                  placeholder="Ej: Propofol 4 mg/kg IV lento"/>
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 6
                }}>
                <TextField label="Mantenimiento" name="anesthesiaProtocol.mantenimiento"
                  value={formData.anesthesiaProtocol?.mantenimiento || ''}
                  onChange={handleInputChange} fullWidth multiline rows={2}
                  placeholder="Ej: Isoflurano 1.5-2%"/>
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 6
                }}>
                <TextField label="Fármacos a evitar" name="anesthesiaProtocol.farmacosEvitar"
                  value={formData.anesthesiaProtocol?.farmacosEvitar || ''}
                  onChange={handleInputChange} fullWidth multiline rows={2}
                  placeholder="Ej: Morfina (alergia), ketamina (cardiopatía)"/>
              </Grid>

              {/* Sección 3: Equipo Quirúrgico */}
              <Grid size={12}>
                <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 2 }} align="left">
                  👨‍⚕️ Equipo Quirúrgico
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  sm: 6
                }}>
                <SearchableSelect
                  label="Cirujano Principal *"
                  name="mainSurgeon"
                  value={formData.mainSurgeonId}
                  onChange={handleTeamChange}
                  required
                  options={professionals.map((p) => ({
                    value: p.id,
                    label: (p.fullName || `${p.nombre || ''} ${p.apellidos || ''}`).trim() || p.email || '—',
                  }))}
                  emptyLabel="Seleccionar..."
                />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  sm: 6
                }}>
                <SearchableSelect
                  label="Anestesiólogo *"
                  name="anesthesiologist"
                  value={formData.anesthesiologistId}
                  onChange={handleTeamChange}
                  required
                  options={professionals.map((p) => ({
                    value: p.id,
                    label: (p.fullName || `${p.nombre || ''} ${p.apellidos || ''}`).trim() || p.email || '—',
                  }))}
                  emptyLabel="Seleccionar..."
                />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  sm: 6
                }}>
                <SearchableSelect
                  label="Cirujano Asistente"
                  name="assistantSurgeon"
                  value={formData.assistantSurgeonId}
                  onChange={handleTeamChange}
                  options={professionals.map((p) => ({
                    value: p.id,
                    label: (p.fullName || `${p.nombre || ''} ${p.apellidos || ''}`).trim() || p.email || '—',
                  }))}
                  emptyLabel="No asignado"
                />
              </Grid>

              {/* Sección 4: Procedimiento */}
              <Grid size={12}>
                <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 2 }} align="left">
                  🏥 Procedimiento Quirúrgico
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid size={12}>
                <Autocomplete
                  freeSolo
                  options={procedures}
                  getOptionLabel={(o) => (typeof o === 'string' ? o : o.name || '')}
                  value={formData.procedureType}
                  onChange={handleProcedureSelect}
                  onInputChange={handleProcedureInputChange}
                  isOptionEqualToValue={(opt, val) => (typeof val === 'string' ? opt.name === val : opt.id === val.id)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Procedimiento Quirúrgico *"
                      required
                      fullWidth
                      placeholder="Ej: Osteosíntesis de fémur, Castración, etc."
                      helperText="Seleccione de la base de datos o escriba uno personalizado"
                    />
                  )}
                />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  sm: 6
                }}>
                <TextField
                  select
                  label="Tipo de Procedimiento *"
                  name="procedureCategory"
                  value={formData.procedureCategory}
                  onChange={handleInputChange}
                  fullWidth
                  required
                >
                  {PROCEDURE_CATEGORIES.map((category) => (
                    <MenuItem key={category} value={category}>
                      {category}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid size={12}>
                <TextField
                  label="Descripción Detallada del Procedimiento *"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  fullWidth
                  required
                  multiline
                  rows={3}
                  placeholder="Describa el procedimiento, técnica quirúrgica, objetivos..."
                />
              </Grid>

              <Grid size={12}>
                <TextField
                  label="Complicaciones Potenciales"
                  name="complications"
                  value={formData.complications}
                  onChange={handleInputChange}
                  fullWidth
                  multiline
                  rows={2}
                  placeholder="Riesgos específicos, alergias conocidas, condiciones preexistentes..."
                />
              </Grid>

              {/* Sección 5: Preparación Pre-quirúrgica */}
              <Grid size={12}>
                <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 2 }} align="left">
                  ✅ Preparación Pre-quirúrgica
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  sm: 6
                }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.fasting.confirmed}
                      onChange={handleInputChange}
                      name="fasting.confirmed"
                    />
                  }
                  label="Ayuno Confirmado"
                />
                <TextField
                  label="Horas de Ayuno"
                  name="fasting.hours"
                  value={formData.fasting.hours}
                  onChange={handleInputChange}
                  fullWidth
                  type="number"
                  size="small"
                  sx={{ mt: 1 }}
                />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  sm: 6
                }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.consentSigned}
                      onChange={handleInputChange}
                      name="consentSigned"
                    />
                  }
                  label="Consentimiento Informado Firmado"
                />
              </Grid>

              <Grid size={12}>
                <Typography variant="subtitle2" gutterBottom>Exámenes Pre-operatorios:</Typography>
              </Grid>

              {(latestExams.lab || latestExams.imaging) ? (
                <Grid size={12}>
                  <Grid container spacing={2}>
                    {latestExams.lab && (
                      <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                        <Paper elevation={0} sx={{ p: 2, border: '1px solid #E2E8F0', borderRadius: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <Box>
                              <Typography variant="body2" fontWeight={700} color={TEAL}>
                                {latestExams.lab.examTypeName || 'Laboratorio'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" display="block">
                                {latestExams.lab.uploadedAt?.toDate
                                  ? latestExams.lab.uploadedAt.toDate().toLocaleDateString('es-CL')
                                  : '—'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                {(latestExams.lab.extractedParameters || []).filter(p => {
                                  const v = parseFloat(p.value);
                                  const min = parseFloat(p.refMin);
                                  const max = parseFloat(p.refMax);
                                  return !isNaN(v) && ((!isNaN(min) && v < min) || (!isNaN(max) && v > max));
                                }).length} parámetro(s) alterado(s)
                              </Typography>
                            </Box>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => setExamDialog({ open: true, exam: latestExams.lab })}
                            >
                              Ver informe
                            </Button>
                          </Box>
                        </Paper>
                      </Grid>
                    )}
                    {latestExams.imaging && (
                      <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                        <Paper elevation={0} sx={{ p: 2, border: '1px solid #E2E8F0', borderRadius: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <Box>
                              <Typography variant="body2" fontWeight={700} color={TEAL}>
                                {latestExams.imaging.examName || 'Imagen'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" display="block">
                                {latestExams.imaging.order?.reportGeneratedAt?.toDate
                                  ? latestExams.imaging.order.reportGeneratedAt.toDate().toLocaleDateString('es-CL')
                                  : (latestExams.imaging.approvedAt?.toDate
                                    ? latestExams.imaging.approvedAt.toDate().toLocaleDateString('es-CL')
                                    : '—')}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                {latestExams.imaging.overallImpression
                                  ? latestExams.imaging.overallImpression.slice(0, 80) + (latestExams.imaging.overallImpression.length > 80 ? '…' : '')
                                  : 'Sin impresión global'}
                              </Typography>
                            </Box>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => setExamDialog({ open: true, exam: latestExams.imaging })}
                            >
                              Ver informe
                            </Button>
                          </Box>
                        </Paper>
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              ) : (
                <Grid size={12}>
                  <Alert severity="info" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                    No se encontraron exámenes de laboratorio ni imágenes aprobados para este paciente.
                  </Alert>
                </Grid>
              )}

              <Grid size={12} sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary" gutterBottom>Seguimiento manual de exámenes solicitados:</Typography>
              </Grid>
              {[{ key: 'hemograma', label: 'Hemograma' }, { key: 'bioquimico', label: 'Bioquímico' },
                { key: 'rayosX', label: 'Rx Tórax/Abd.' }, { key: 'ecografia', label: 'Ecografía' }, { key: 'ecg', label: 'ECG' }
              ].map(exam => (
                <Grid
                  key={exam.key}
                  size={{
                    xs: 12,
                    sm: 6,
                    md: 4
                  }}>
                  <Paper elevation={0} sx={{ p: 1.5, border: '1px solid #E2E8F0', borderRadius: 2 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <FormControlLabel
                        control={<Checkbox size="small"
                          checked={formData.preOpExams[exam.key]?.solicitado || false}
                          onChange={e => handleNestedChange('preOpExams', exam.key, 'solicitado', e.target.checked)}/>}
                        label={<Typography variant="body2" fontWeight={600}>{exam.label}</Typography>}/>
                      {formData.preOpExams[exam.key]?.solicitado && (
                        <FormControlLabel
                          control={<Checkbox size="small" color="success"
                            checked={formData.preOpExams[exam.key]?.disponible || false}
                            onChange={e => handleNestedChange('preOpExams', exam.key, 'disponible', e.target.checked)}/>}
                          label={<Typography variant="caption" color="success.main">Disponible</Typography>}/>
                      )}
                    </Stack>
                    {formData.preOpExams[exam.key]?.solicitado && !formData.preOpExams[exam.key]?.disponible && (
                      <Alert severity="warning" sx={{ py: 0.3, fontSize: '0.72rem', mt: 0.5 }}>Sin resultado disponible</Alert>
                    )}
                  </Paper>
                </Grid>
              ))}

              {/* Sección 6: Recursos */}
              <Grid size={12}>
                <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 2 }} align="left">
                  🔧 Recursos Necesarios
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  sm: 6
                }}>
                <TextField
                  label="Equipamiento Especial"
                  name="equipment"
                  value={formData.equipment}
                  onChange={handleInputChange}
                  fullWidth
                  multiline
                  rows={2}
                  placeholder="Ej: Taladro ortopédico, microscopio quirúrgico..."
                />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  sm: 6
                }}>
                <TextField
                  label="Materiales Requeridos"
                  name="materials"
                  value={formData.materials}
                  onChange={handleInputChange}
                  fullWidth
                  multiline
                  rows={2}
                  placeholder="Ej: Placas, tornillos, suturas específicas..."
                />
              </Grid>

              {/* Sección 7: Estimaciones */}
              <Grid size={12}>
                <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 2 }} align="left">
                  💰 Estimaciones
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  sm: 4
                }}>
                <TextField
                  label="Costo Estimado (CLP)"
                  name="estimatedCost"
                  value={formData.estimatedCost}
                  onChange={handleInputChange}
                  fullWidth
                  type="number"
                  placeholder="450000"
                />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  sm: 4
                }}>
                <TextField
                  label="Recuperación (días)"
                  name="recoveryDays"
                  value={formData.recoveryDays}
                  onChange={handleInputChange}
                  fullWidth
                  type="number"
                />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  sm: 4
                }}>
                <TextField
                  label="Hospitalización (días)"
                  name="hospitalizationDays"
                  value={formData.hospitalizationDays}
                  onChange={handleInputChange}
                  fullWidth
                  type="number"
                />
              </Grid>

              {/* Resumen */}
              <Grid size={12}>
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom align="left">
                    📋 Resumen:
                  </Typography>
                  <Typography variant="body2">
                    Cirugía de <strong>{formData.procedureType || '...'}</strong> para{' '}
                    <strong>{formData.patientName || '...'}</strong>
                    <br />
                    Programada para el{' '}
                    <strong>
                      {formData.date ? new Date(formData.date).toLocaleDateString('es-CL') : '...'}
                    </strong>{' '}
                    a las <strong>{formData.startTime}</strong>
                    <br />
                    Pabellón:{' '}
                    <strong>
                      {operatingRooms.find((r) => r.id === formData.operatingRoom)?.name || '...'}
                    </strong>
                    {formData.surgicalTable && (
                      <>
                        {' '}
                        - Mesa: <strong>{formData.surgicalTable}</strong>
                      </>
                    )}
                    <br />
                    Duración estimada: <strong>{formData.duration} minutos</strong>
                    <br />
                    Cirujano: <strong>{formData.mainSurgeon || '...'}</strong>
                    <br />
                    {formData.ownerConvenio && (
                      <>
                        Convenio: <strong>{formData.ownerConvenio}</strong>
                        <br />
                      </>
                    )}
                    {formData.budgetNumber && (
                      <>
                        N° Presupuesto: <strong>{formData.budgetNumber}</strong>
                        {formData.presupuestoEstado && formData.presupuestoEstado !== 'pagado' && (
                          <span style={{ color: '#D97706', fontWeight: 700 }}> · ⚠ NO PAGADO ({formData.presupuestoEstado})</span>
                        )}
                      </>
                    )}
                  </Typography>
                </Alert>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button onClick={handleSaveSurgery} variant="contained" color="primary" size="large">
            Programar Cirugía
          </Button>
        </DialogActions>
      </Dialog>
      <DayListDrawer open={dayListOpen} onClose={() => setDayListOpen(false)}
        surgeries={surgeries} operatingRooms={operatingRooms}/>
      <SafetyChecklistDialog open={checklistDlg.open}
        onClose={() => setChecklistDlg({ open: false, surgery: null })}
        surgery={checklistDlg.surgery} onSave={handleSaveChecklist} userData={userData}/>
      <Menu
        open={Boolean(statusMenuAnchor)}
        onClose={handleCloseStatusMenu}
        anchorReference="anchorPosition"
        anchorPosition=
          {statusMenuAnchor
            ? { top: statusMenuAnchor.mouseY, left: statusMenuAnchor.mouseX }
            : undefined
          }
      >
        {SURGERY_STATES.map((state) => (
          <MenuItem
            key={state.value}
            selected={statusMenuSurgery?.status === state.value}
            onClick={() => handleChangeSurgeryStatus(state.value)}
          >
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                bgcolor: state.color,
                mr: 1,
              }}
            />
            {state.label}
          </MenuItem>
        ))}
      </Menu>
      <Dialog open={examDialog.open} onClose={() => setExamDialog({ open: false, exam: null })} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: PRIMARY }}>
          {examDialog.exam?.examTypeName || examDialog.exam?.examName || 'Informe completo'}
        </DialogTitle>
        <DialogContent dividers>
          {examDialog.exam?.extractedParameters ? (
            <Stack spacing={1}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Parámetros registrados
              </Typography>
              {examDialog.exam.extractedParameters.map((p, i) => {
                const v = parseFloat(p.value);
                const min = parseFloat(p.refMin);
                const max = parseFloat(p.refMax);
                const altered = !isNaN(v) && ((!isNaN(min) && v < min) || (!isNaN(max) && v > max));
                return (
                  <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5, borderBottom: '1px solid #E2E8F0' }}>
                    <Typography variant="body2">{p.paramName}</Typography>
                    <Typography variant="body2" fontWeight={altered ? 700 : 400} color={altered ? 'error' : 'text.primary'}>
                      {p.value || '—'} {p.unit || ''} {altered ? '⚠' : ''}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 80, textAlign: 'right' }}>
                      Ref: {p.refMin || '—'} - {p.refMax || '—'}
                    </Typography>
                  </Box>
                );
              })}
            </Stack>
          ) : (
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Impresión global</Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                {examDialog.exam?.overallImpression || 'Sin impresión global'}
              </Typography>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>Hallazgos</Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                {examDialog.exam?.findings || 'Sin hallazgos detallados'}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExamDialog({ open: false, exam: null })}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={calendarDialog.open} onClose={() => setCalendarDialog({ open: false, date: '', roomId: '', startTime: '09:00' })} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: PRIMARY }}>Calendario de cirugías</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <TextField
              type="date"
              label="Fecha"
              value={calendarDialog.date}
              onChange={(e) => setCalendarDialog(prev => ({ ...prev, date: e.target.value, roomId: '', startTime: '' }))}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <Box sx={{ border: '1px solid #E2E8F0', borderRadius: 2, overflow: 'hidden' }}>
              <Box sx={{ display: 'flex', bgcolor: PRIMARY, color: 'white', p: 1, fontWeight: 700, fontSize: '0.75rem' }}>
                <Box sx={{ width: 56 }}>Hora</Box>
                {filteredOperatingRooms.map((r) => (
                  <Box key={r.id} sx={{ flex: 1, textAlign: 'center' }}>{r.name}</Box>
                ))}
              </Box>
              {WORK_HOURS.map((hour) => {
                const hh = `${hour.toString().padStart(2, '0')}:00`;
                return (
                  <Box key={hour} sx={{ display: 'flex', borderBottom: '1px solid #E2E8F0', minHeight: 44, alignItems: 'stretch' }}>
                    <Box sx={{ width: 56, p: 1, fontSize: '0.75rem', display: 'flex', alignItems: 'center' }}>{hh}</Box>
                    {filteredOperatingRooms.map((room) => {
                      const s = calendarSurgeries.find((x) => {
                        if (x.operatingRoom !== room.id) return false;
                        const startH = parseInt((x.startTime || '00:00').split(':')[0]);
                        const endTime = x.endTime || calculateEndTime(x.startTime, x.duration);
                        const endH = parseInt(endTime.split(':')[0]) + (parseInt(endTime.split(':')[1]) > 0 ? 1 : 0);
                        return hour >= startH && hour < endH;
                      });
                      const selected = calendarDialog.roomId === room.id && calendarDialog.startTime === hh;
                      return (
                        <Box
                          key={room.id}
                          onClick={() => { if (!s) setCalendarDialog(prev => ({ ...prev, roomId: room.id, startTime: hh })); }}
                          sx={{
                            flex: 1,
                            p: 1,
                            cursor: s ? 'default' : 'pointer',
                            bgcolor: selected ? '#e0f2fe' : (s ? '#fee2e2' : 'transparent'),
                            borderLeft: '1px solid #E2E8F0',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.7rem',
                            fontWeight: selected ? 700 : 400,
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            textOverflow: 'ellipsis'
                          }}
                        >
                          {s ? s.patientName : (selected ? '✓' : '')}
                        </Box>
                      );
                    })}
                  </Box>
                );
              })}
            </Box>
            {calendarDialog.roomId && calendarDialog.startTime && (
              <Alert severity="info" sx={{ fontSize: '0.8rem' }}>
                Seleccionado: <strong>{filteredOperatingRooms.find(r => r.id === calendarDialog.roomId)?.name || operatingRooms.find(r => r.id === calendarDialog.roomId)?.name}</strong> a las <strong>{calendarDialog.startTime}</strong>
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCalendarDialog({ open: false, date: '', roomId: '', startTime: '09:00' })}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={confirmCalendarSelection}
            disabled={!calendarDialog.date || !calendarDialog.roomId || !calendarDialog.startTime}
          >
            Confirmar selección
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={snackbar.severity === 'warning' ? 10000 : 6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity} 
          sx={{ 
            width: '100%',
            '& .MuiAlert-message': {
              fontSize: snackbar.severity === 'warning' ? '1rem' : '0.875rem',
              fontWeight: snackbar.severity === 'warning' ? 'bold' : 'normal'
            }
          }}
          variant={snackbar.severity === 'warning' ? 'filled' : 'standard'}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SurgeryScheduler;
